import gql from 'graphql-tag';

import { Cache, cacheExchange, KeyingConfig } from '@urql/exchange-graphcache';
import { DocumentNode } from 'graphql';
import { Exchange } from 'urql';

import { removeOrgSuffixFromSlug } from '@watershed/shared-universal/calculationCustomization';
import { createSupplierId } from '@watershed/shared-universal/utils/SuppliersUtils';
import isNotNullish from '@watershed/shared-util/src/isNotNullish';
import isNullish from '@watershed/shared-util/src/isNullish';

import {
  GQAcceptOrgStructureVersionUploadMutation,
  GQAcceptOrgStructureVersionUploadMutationVariables,
  GQAddDataApprovalCommentMutation,
  GQAddDataApprovalCommentMutationVariables,
  GQAddEngagementTaskCommentMutation,
  GQAddEngagementTaskCommentMutationVariables,
  GQApproveDataMutation,
  GQApproveDataMutationVariables,
  GQArchiveCalculationCitationMutation,
  GQArchiveCalculationCustomizationMutation,
  GQArchivedCalculationCitationsForOrgQuery,
  GQAssignMeasurementTaskMutation,
  GQAssignMeasurementTaskMutationVariables,
  GQBatchUpdateReportQuestionAssigneesMutation,
  GQBatchUpdateReportQuestionAssigneesMutationVariables,
  GQCalculationCitationsForOrgQuery,
  GQCalculationCustomizationsQuery,
  GQCancelFootprintReviewRequestMutation,
  GQCancelFootprintReviewRequestMutationVariables,
  GQCheckAndGenerateFacilitiesBartsMutation,
  GQClimateProgramProjectsForHomepageQuery,
  GQCreateAssetCorporateMutation,
  GQCreateAssetGroupMutation,
  GQCreateAssetHoldingMutation,
  GQCreateAssetHoldingMutationVariables,
  GQCreateAssetPersonalMotorVehicleInsuranceMutation,
  GQCreateAssetRealEstateMutation,
  GQCreateAssetSovereignBondMutation,
  GQCreateBiDashboardMutation,
  GQCreateBiDashboardSavedViewMutation,
  GQCreateCalculationCitationMutation,
  GQCreateClimateProgramProjectForMeasurementMutation,
  GQCreateClimateProgramProjectMutation,
  GQCreateDefaultOrgStructureVersionMutation,
  GQCreateDefaultOrgStructureVersionMutationVariables,
  GQCreateEngagementCohortMutation,
  GQCreateEngagementCohortMutationVariables,
  GQCreateEngagementCohortsForCompanyMutation,
  GQCreateEngagementCohortsForCompanyMutationVariables,
  GQCreateFinanceReportingTagsMutation,
  GQCreateFinanceReportingTagsMutationVariables,
  GQCreateFinanceTagMutation,
  GQCreateFinanceTagMutationVariables,
  GQCreateFundMutation,
  GQCreateOrgUnitMutation,
  GQCreateOrgUnitMutationVariables,
  GQCreateOrgUnitRelationshipMutation,
  GQCreateOrgUnitRelationshipMutationVariables,
  GQCreateOrgUnitTypeMutation,
  GQCreateOrgUnitTypeMutationVariables,
  GQCreateOrgUnitTypeRelationshipMutation,
  GQCreateOrgUnitTypeRelationshipMutationVariables,
  GQCreatePrivateDisclosureMutation,
  GQCreateReferenceTableMutation,
  GQCreateSupplierAttachmentsMutation,
  GQCreateSupplierAttachmentsMutationVariables,
  GQCreateSupplierContactMutation,
  GQCreateSupplierContactMutationVariables,
  GQCreateSupplierSavedViewMutation,
  GQCreateSupplierSavedViewMutationVariables,
  GQCreateSupplierScoreCriteriaMutation,
  GQCreateSupportCaseCommentMutation,
  GQCustomActivityTypesQuery,
  GQDeleteAssetHoldingMutation,
  GQDeleteAssetHoldingMutationVariables,
  GQDeleteAssetsMutation,
  GQDeleteBiDashboardMutation,
  GQDeleteBuildingSupportingDocumentMutation,
  GQDeleteBuildingSupportingDocumentMutationVariables,
  GQDeleteCompanyEngagementCohortMutation,
  GQDeleteCompanyEngagementCohortMutationVariables,
  GQDeleteFileMutation,
  GQDeleteFileMutationVariables,
  GQDeleteFinanceTagMutation,
  GQDeleteFinanceTagMutationVariables,
  GQDeleteFinanceTagsMutation,
  GQDeleteFinanceTagsMutationVariables,
  GQDeleteMaterialityAssessmentMutation,
  GQDeleteMaterialVariantsMutation,
  GQDeleteMaterialVariantsMutationVariables,
  GQDeleteNonFootprintSupplierMutation,
  GQDeleteNonFootprintSupplierMutationVariables,
  GQDeleteOrgCompanyMutation,
  GQDeleteOrgCompanyMutationVariables,
  GQDeleteOrgStructureVersionMutation,
  GQDeleteOrgStructureVersionMutationVariables,
  GQDeleteOrgUnitsMutation,
  GQDeleteOrgUnitsMutationVariables,
  GQDeleteOrgUnitTypeMutation,
  GQDeleteOrgUnitTypeMutationVariables,
  GQDeleteOrgUnitTypeRelationshipMutation,
  GQDeleteOrgUnitTypeRelationshipMutationVariables,
  GQDeletePrivateDisclosureTargetByIdMutation,
  GQDeleteSnapshotMutation,
  GQDeleteSupplierAttachmentMutation,
  GQDeleteSupplierAttachmentMutationVariables,
  GQDeleteSupplierContactMutation,
  GQDeleteSupplierContactMutationVariables,
  GQDeleteSupplierSavedViewMutation,
  GQDeleteSupplierSavedViewMutationVariables,
  GQDeleteSupplierTableColumnMutation,
  GQDeleteSupplierTableColumnMutationVariables,
  GQDuplicateOrgStructureVersionMutation,
  GQDuplicateOrgStructureVersionMutationVariables,
  GQFinanceSettingsQuery,
  GQFinanceSnapshotsQuery,
  GQFootprintsForArchivedFootprintListQuery,
  GQFootprintsForFootprintListQuery,
  GQFootprintsForFootprintSelectorQuery,
  GQGenerateBartFromFileUploadMutation,
  GQGenerateBartFromFileUploadMutationVariables,
  GQGenerateFootprintV2Mutation,
  GQGenerateFootprintV2MutationVariables,
  GQGetAllAssignableUsersQuery,
  GQGetBiDashboardQuery,
  GQGetCdpWorkflowSyncStatusQuery,
  GQGetContactsForCompanyQuery,
  GQGetContactsForOrgQuery,
  GQGetEngagementCohortsQuery,
  GQGetJobResultsQuery,
  GQGetMaterialVariantsQuery,
  GQGetSupplierAttachmentsQuery,
  GQGetSupplierColumnsForCustomFieldQuery,
  GQGetSupplierDetailsDataQuery,
  GQGetSupplierViewsQuery,
  GQMapMaterialVariantsToAbstractMaterialsMutationVariables,
  GQMaterialityPageQuery,
  GQMaterialTableFieldsFragment,
  GQNewMeasurementWizardQuery,
  GQOrgStructureVersionsQuery,
  GQOrgUnitTypeQuery,
  GQPrepareActivityDataAndRunFootprintMutation,
  GQPrepareActivityDataAndRunFootprintMutationVariables,
  GQReferenceTablesForOrgPaginatedQuery,
  GQRejectDataMutation,
  GQRejectDataMutationVariables,
  GQRequestDataApprovalMutation,
  GQRequestDataApprovalMutationVariables,
  GQRequestUnlockDataMutation,
  GQRequestUnlockDataMutationVariables,
  GQSelectLcaForMaterialVariantMutation,
  GQSetApproversMutation,
  GQSetApproversMutationVariables,
  GQSetEngagementCohortAndMembersMutation,
  GQSetEngagementCohortsForCompanyMutation,
  GQSetPreferredLocaleMutation,
  GQSetPreferredLocaleMutationVariables,
  GQSetPublishEngagementTaskConfigMutation,
  GQStartFlashForMaterialVariantMutation,
  GQStartMaterialMappingForMaterialVariantsMutation,
  GQStartMaterialMappingForUpdatedVariantsMutation,
  GQSupplierScorecardsQuery,
  GQSupportCasePageQuery,
  GQUnarchiveCalculationCitationMutation,
  GQUnarchiveCalculationCustomizationMutation,
  GQUnlockDataMutation,
  GQUnlockDataMutationVariables,
  GQUpdateBiDashboardMutation,
  GQUpdateBiDashboardSavedViewMutation,
  GQUpdateFinanceAutomaticRemindersMutation,
  GQUpdateFootprintReviewRequestStatusMutation,
  GQUpdateFootprintReviewRequestStatusMutationVariables,
  GQUpdateMaterialityAssessmentMutation,
  GQUpdateOrgProfileMutation,
  GQUpdateOrgProfileMutationVariables,
  GQUpdatePrivateDisclosureMutation,
  GQUpdateSupplierContactMutation,
  GQUpdateSupplierContactMutationVariables,
  GQUpdateSupplierCustomDataMutation,
  GQUpdateSupplierCustomDataMutationVariables,
  GQUpdateSupplierSavedViewMutation,
  GQUpdateSupplierSavedViewMutationVariables,
  GQUpdateSupportCaseMutation,
  GQUpdateSupportCaseMutationVariables,
  GQUpsertNonFootprintSupplierMutation,
  GQUpsertNonFootprintSupplierMutationVariables,
  GQUpsertOrgCompanyForNameMutation,
  GQUpsertOrgCompanyForNameMutationVariables,
  GQUpsertOrgStructureVersionMutation,
  GQUpsertOrgStructureVersionMutationVariables,
  GQUpsertReportQuestionInstancesAccessMutationVariables,
} from '@watershed/app-dashboard/generated/graphql-operations';
import {
  GQActivityRecord,
  GQArchiveFootprintPayload,
  GQBackgroundJobState,
  GQBiTableReference,
  GQBusinessActivityTypeWithReleases,
  GQCreateCustomActivityTypePayload,
  GQCreateFootprintPayload,
  GQCreateUserPayload,
  GQCreateWatershedFootprintReviewRequestPayload,
  GQDeleteClimateProgramCalendarEventPayload,
  GQDeleteClimateProgramTimelineProjectPayload,
  GQDeleteFundsPayload,
  GQEvaluatedEmissionsModelsResults,
  GQFrozenDatasourceSummary,
  GQMethodologyCustomizationColumnDisplayFormat,
  GQMethodologyCustomizationDisplayRow,
  GQMethodologyCustomizationDisplaySection,
  GQMethodologyCustomizationMissingSourceDataConfig,
  GQMutationBulkGrantUserRoleArgs,
  GQMutationBulkRevokeUserRoleArgs,
  GQMutationDeleteApiKeyArgs,
  GQMutationDeleteOauthClientArgs,
  GQMutationDeletePeerCompanyGroupArgs,
  GQMutationDeletePlanArgs,
  GQMutationDeleteRoleArgs,
  GQMutationGrantUserRoleArgs,
  GQMutationRevokeUserRoleArgs,
  GQMutationUpsertReportsAccessArgs,
  GQNetZeroSummaryYear,
  GQObjectType,
  GQProductionGraphNode,
  GQRenameFootprintPayload,
  GQSimpleTimeseries,
  GQSupplierViewChartOrder,
  GQUnarchiveFootprintPayload,
  GQUpsertReportAnswerInput,
} from '@watershed/app-dashboard/generated/graphql-schema-types';
import {
  ActiveOrganizationDocument,
  ArchivedCalculationCitationsForOrgDocument,
  CalculationCitationsForOrgDocument,
  CalculationCustomizationsDocument,
  ClimateProgramProjectsForHomepageDocument,
  CustomActivityTypesDocument,
  EngagementTaskContentsFragmentDoc,
  FinanceSettingsDocument,
  FinanceSnapshotsDocument,
  FootprintsForArchivedFootprintListDocument,
  FootprintsForFootprintListDocument,
  FootprintsForFootprintSelectorDocument,
  GetAllAssignableUsersDocument,
  GetBiDashboardDocument,
  GetContactsForCompanyDocument,
  GetContactsForOrgDocument,
  GetEngagementCohortsDocument,
  GetMaterialVariantsDocument,
  GetSupplierAttachmentsDocument,
  GetSupplierColumnsForCustomFieldDocument,
  GetSupplierDetailsDataDocument,
  GetSupplierViewsDocument,
  MaterialityPageDocument,
  MaterialTableFieldsFragmentDoc,
  NewMeasurementWizardDocument,
  OrgStructureVersionsDocument,
  OrgUnitTypeDocument,
  ReferenceTablesForOrgPaginatedDocument,
  SupplierScorecardsDocument,
  SupportCasePageDocument,
} from '@watershed/app-dashboard/generated/urql';

import {
  patchSupplierInCache,
  updateSuppliersDataFragment,
} from '../components/footprint/suppliers/utils/cacheUtils';
import {
  updateDataApprovalObjectChangelogCache,
  updateDataApprovalUserUploadTaskChangelogCache,
} from '../components/measure/datasources/datasourceId/approvalFlow/utils/dataApprovalsViewUtils';
import { updateMaterialVariantsRunProgressCache } from './cacheUtils/productFootprintCacheUtils';
import { mergeCancelWatershedFootprintReviewRequestIntoFootprintSelector } from './cacheUtils/updateCacheAfterCancelWatershedFootprintReviewRequest';
import { updateCacheAfterCreateWatershedFootprintReviewRequest } from './cacheUtils/updateCacheAfterCreateWatershedFootprintReviewRequest';
import { updateCacheAfterFootprintGeneration } from './cacheUtils/updateCacheAfterFootprintGeneration';
import { mergeWatershedFootprintReviewRequestIntoFootprintSelector } from './cacheUtils/updateCacheAfterUpdateWatershedFootprintReviewRequestStatus';
import {
  CachedSupplierMappingTableRowAction,
  updateCachedSupplierMappingRows,
} from './supplyChain/supplierMappingGraphCacheUtils';

type BasicIntrospectionQuery = { __schema: any };

export function invalidateAllTopLevelQueriesForNode(
  cache: Cache,
  topLevelQueryName: string
) {
  const allFields = cache.inspectFields('Query');
  const queries = allFields.filter((x) => x.fieldName === topLevelQueryName);
  for (const query of queries) {
    const { fieldName, arguments: variables } = query;
    cache.invalidate('Query', fieldName, variables || undefined);
  }
}

function invalidateDeletedRecords(
  cache: Cache,
  deletedRecords: Array<string>,
  typename: string
) {
  if (deletedRecords) {
    deletedRecords.forEach((record) => {
      cache.invalidate({
        __typename: typename,
        id: record,
      });
    });
  }
}

function patchFragmentInCache({
  cache,
  fragment,
  maybeIdToUpdate,
  patch,
}: {
  cache: Cache;
  fragment: DocumentNode;
  maybeIdToUpdate: string | undefined;
  patch: (current: any) => any;
}) {
  if (!maybeIdToUpdate) {
    return;
  }

  const currentValue = cache.readFragment(fragment, {
    id: maybeIdToUpdate,
  });

  if (!currentValue) {
    return;
  }
  cache.writeFragment(fragment, patch(currentValue));
}

const FundList = gql`
  # eslint-disable-next-line @watershed/gql-operations-must-assign-owners
  query FundList {
    funds {
      __typename
      id
    }
  }
`;
const AssetCorporateList = gql`
  # eslint-disable-next-line @watershed/gql-operations-must-assign-owners
  query AssetCorporateList {
    assetsCorporate {
      id
    }
  }
`;
const AssetRealEstateList = gql`
  # eslint-disable-next-line @watershed/gql-operations-must-assign-owners
  query AssetRealEstateList {
    assetsRealEstate {
      id
    }
  }
`;
const AssetGroupList = gql`
  # eslint-disable-next-line @watershed/gql-operations-must-assign-owners
  query AssetGroupList {
    assetGroups {
      id
    }
  }
`;
const AssetSovereignBondList = gql`
  # eslint-disable-next-line @watershed/gql-operations-must-assign-owners
  query AssetSovereignBondList {
    assetSovereignBonds {
      id
    }
  }
`;
const AssetPersonalMotorVehicleInsuranceList = gql`
  # eslint-disable-next-line @watershed/gql-operations-must-assign-owners
  query AssetPersonalMotorVehicleInsuranceList {
    assetPersonalMotorVehicleInsurances {
      id
    }
  }
`;

// This type is used to ensure that we have a cache key override for each type
// in GQBiTableReference
type BiTableReferenceTypes = NonNullable<GQBiTableReference['__typename']>;
type BiTableKeyingConfig = Required<Pick<KeyingConfig, BiTableReferenceTypes>>;
const biTableReferenceKeyConfigs: BiTableKeyingConfig = {
  FootprintSnapshotTableReference: () => null,
  FinanceFootprintSnapshotTableReference: () => null,
  FootprintBartTableReference: () => null,
  FootprintSidecarTableReference: () => null,
  BartTableReference: () => null,
  SyncedPostgresTableReference: () => null,
  BartInstanceTableReference: () => null,
  DataRegistryTableReference: () => null,
  LineageAssetFileMetadataTableReference: () => null,
  ActivityDatasetVersionTableReference: () => null,
  ReferenceDataTableReference: () => null,
  ProductFootprintTableReference: () => null,
};

/**
 * Builds a cache exchange for the given schema.
 *
 * Accepts a callback for when the locale changes as a temporary measure to
 * ensure the locale header for subsequent queries is set immediately -- i.e.
 * doesn't wait for a state change in the locale atom to be committed. Once some
 * teething issues with our GQL setup are resolved (we've migrated to using
 * suspense, and urql provides a way to tell when cache invalidations are
 * settled), we should be able to remove this awkward callback behaviour.
 */
const cache: (
  schema: BasicIntrospectionQuery,
  onSetLocale: (locale: string) => void
) => Exchange = (schema, onSetLocale = () => {}) =>
  cacheExchange({
    keys: {
      // Returning null means graphcache will skip normalizing these types.
      // See https://formidable.com/open-source/urql/docs/graphcache/normalized-caching/
      FacilitiesMetadata: () => null,
      Footprint: () => null,
      FootprintSummary: () => null,
      FootprintAnalysisTimeSeriesDataPoint: () => null,
      FootprintAnalysisTimeseriesSeries: () => null,
      TimeseriesForGroup: () => null,
      FootprintSummaryGroups: () => null,
      CustomIntensityDataPoint: () => null,
      Vendor: () => null,
      FootprintAnalysis: () => null,
      ConversionChainItemForDashboard: () => null,
      ConversionFactorForDashboard: () => null,
      CalculationChain: () => null,
      ActivityRecord: (initialData) => {
        const data = initialData as GQActivityRecord;
        return data.stableId;
      },
      BusinessActivityTypeWithReleases: (initialData) => {
        const data = initialData as GQBusinessActivityTypeWithReleases;
        return data.businessActivityType;
      },
      BiSavedViewSnapshotQueryContext: () => null,
      BiDynamicNormalizingMeasure: () => null,
      BiDynamicNormalizingQuery: () => null,
      BiQueryFilter: () => null,
      TraceDetails: () => null,
      CalculationDataLineagePayload: () => null,
      UutRowsDataLineage: () => null,
      FileDataLineage: () => null,
      FilterExpressionGroup: () => null,
      FilterExpressionPrimitive: () => null,
      FilterExpressionGroupWithNewFilters: () => null,
      FilterExpressionPrimitiveWithNewFilters: () => null,
      IntensityDenominatorDataPoint: () => null,
      IntensityDenominatorRecord: () => null,
      PlanVariables: () => null,
      PlanCarbonFund: () => null,

      EnergyConsumptionBreakdown: () => null,
      EnergyConsumptionDataPoint: () => null,
      FootprintFieldValue: () => null,

      BenchmarkBreakdownDataPoint: () => null,
      BenchmarkScopeBreakdownDataPoint: () => null,
      NetZeroSummary: () => null,
      NetZeroSummaryYear: (initialData) => {
        const data = initialData as unknown as GQNetZeroSummaryYear;
        return `${data.year}-${data.kind}`;
      },
      MethodologyCustomizationColumnDisplayFormat: (initialData) => {
        const data =
          initialData as GQMethodologyCustomizationColumnDisplayFormat;
        return data.parquetColumnName;
      },
      MethodologyCustomizationMissingSourceDataConfig: (initialData) => {
        const data =
          initialData as GQMethodologyCustomizationMissingSourceDataConfig;
        return data.message;
      },
      MethodologyCustomizationDisplaySection: (initialData) => {
        const data = initialData as GQMethodologyCustomizationDisplaySection;
        return data.sectionName;
      },
      MethodologyCustomizationDisplayRow: (initialData) => {
        // Dependent on both the customization type and the mapping config ids, but not type config, but mapping config isn't always requested
        const data = initialData as GQMethodologyCustomizationDisplayRow;
        return `${data.customizationTypeId}-${data.mappingConfigIds?.join(
          '-'
        )}`;
      },
      CategorizedEmissionData: () => null,
      SimpleTimeseries: (data) =>
        (data as Partial<GQSimpleTimeseries>).id ?? null,
      MarkdownCardInfo: () => null,
      ClimateProgramInfo: () => null,
      ClimateProgramInfoStep: () => null,
      ClimateProgramInfoTarget: () => null,
      ReductionTargetsInfo: () => null,
      ReductionTargetsInfoItem: () => null,
      Disclosure: () => null,
      ReportQuestionSeriesMetricItem: () => null,
      PeerExternalReportAnswers: () => null,
      PeerExternalReportAnswersForIdentifiersPayload: () => null,
      UserUploadedTableSchemaColumn: () => null,
      UserUploadedTableDataPreview: () => null,
      UserUploadedTableRoboCleanerResult: () => null,
      UserUploadedTableUserReviewMetadata: () => null,
      ColumnMapping: () => null,
      UserUploadedTableCell: () => null,
      YearMonthInterval: () => null,
      UserRoleAssignment: () => null,
      QuantityTrendAnomaly: () => null,
      FinancialsAccount: () => null,
      UserUploadedTableParseConfig: () => null,
      CanonicalSchemaExampleRowFields: () => null,
      CanonicalSchemaExampleData: () => null,
      IngestionQuestionAnswer: () => null,
      IngestionQuestionResponse: () => null,
      IngestionQuestionOption: () => null,
      IngestionExampleDataRow: () => null,
      IngestionExampleDataColumn: () => null,
      IngestionExampleDataRowField: () => null,
      AdditionalQuestionResponse: () => null,
      MeasurementProjectDatasetProgress: () => null,

      FinanceSectorEmissions: () => null,
      FinanceAggregateMetric: () => null,
      FootprintExclusionRuleTargetedEmissions: () => null,
      ForecastPeriod: () => null,
      ForecastPeriodPoint: () => null,
      FrozenDatasourceSummary: (data) =>
        (data as GQFrozenDatasourceSummary).signedUrl,
      CurrencyOverride: () => null,
      SerializableError: () => null,
      Timeseries: () => null,
      LabeledTimeseries: () => null,
      SuppliersWithInterval: () => null,
      SupplierTable: () => null,
      SupplierViewColumnOrder: () => null,
      SupplierViewChartOrder: (data) =>
        (data as GQSupplierViewChartOrder).chartId,
      SupplierHistoricalEmissions: () => null,
      SuppliersSettings: () => 'suppliersSettings', // Only one instance
      SurveyDefinitionAndStatus: () => null,
      UserInputtedForecastPeriodPoint: () => null,
      FootprintEstimateOutputByScope: () => null,
      InstructionsBundle: () => null,
      DependentReportItems: () => null,
      ResolvedReportQuestionInputsSuccess: () => null,
      ResolvedReportQuestionInputsFailure: () => null,
      RenewableEnergyPercentagePoint: () => null,
      InstructionsStep: () => null,
      TabularData: () => null,
      InputQuantityByGroup: () => null,
      MaterialFootprintRowUpdatedTaxonomy: () => null,
      ProductFootprintRowUpdatedTaxonomy: () => null,
      EvaluatedEmissionsModelsResults: (data) => {
        // Combine keys to approximate a unique key for the whole object
        const results = data as GQEvaluatedEmissionsModelsResults;
        const versionIds = results.emissionsModelVersions
          .map((v) => v.id)
          .sort();
        return versionIds.join('-');
      },

      DatasetWithStage: () => null, // the IDs here aren't unique, and this isn't a type we update in the cache

      VectorNameAndValues: () => null,

      DrilldownForReportQuestionPayload: () => null,
      CustomReportControlsPayload: () => null,
      ReportQuestionSingleValueMetric: () => null,
      ReportQuestionSeriesMetric: () => null,
      CdpLatestSyncWorkflowStatusPayload: () => null,
      ReportUserAccess: () => null,
      ReportQuestionInstancePermissionActionContainer: () => null,
      ReportQuestionInstancesAggregate: () => null,
      ReportQuestionInstancesAggregateUserAccess: () => null,
      ReportQuestionInstancesAggregatePermissionActionContainer: () => null,
      ReportAccessOriginWithMetadata: () => null,
      ReportPermissionActionContainer: () => null,
      TraceEmissionsModelVariableDetails: () => null,
      StringKeyValue: () => null,
      FootprintAnalysisCustomIntensityDataPoint: () => null,
      ExampleTracesForFingerprints: () => null,
      EmissionsModelVariableDetails: () => null,
      BartColumnDetails: () => null,
      ReferenceValueDetails: () => null,
      TraceReferenceValueCitationDetails: () => null,
      TraceReferenceValueCitationDetailsOrg: () => null,
      OneSchemaJwtPayload: () => null,
      SdiInstructionsStep: () => null,
      SdiInstructionsFaqType: () => null,
      CadtBuildingsPushPreview: () => null,
      ParquetColumn: () => null,
      UtilitiesMetadataByType: () => null,

      FinanceMeasurementProgressRow: () => null,
      MeasuredFootprintDetails: () => null,
      GridColumnConfigurationSingleSelect: () => null,
      DataGridSortItem: () => null,

      ProductionGraphNode: (data) => {
        const node = data as GQProductionGraphNode;
        return node.identifier;
      },
      ProductionGraphNodeInputRate: () => null,

      // #region BI types without ids.

      BiDimensionListOptionItem: () => null,
      BiDimensionMeta: () => null,
      BiDisplayValueMapping: () => null,
      BiMeasureMeta: () => null,
      BiFieldBooleanMeta: () => null,
      BiFieldStringMeta: () => null,
      BiFieldDateMeta: () => null,
      BiFieldYearMonthMeta: () => null,
      BiFieldNumberMeta: () => null,
      ActivityContextMetadata: () => null,
      BiSchemasResult: () => null,
      BiSchema: () => null,
      BiTableSchemaMapping: () => null,
      ...biTableReferenceKeyConfigs,
      // #endregion
    },
    updates: {
      Query: {
        backgroundJob: (result: GQGetJobResultsQuery, _args, cache, _info) => {
          // Invalidate things here that need to be invalidated after some background job finishes
          if (result?.job?.state !== GQBackgroundJobState.Done) {
            return;
          }
          switch (result?.job?.kind) {
            case 'pushUtilitiesDiffWorkflow': {
              // Invalidates facilities and utilities
              invalidateAllTopLevelQueriesForNode(cache, 'facilities');
              invalidateAllTopLevelQueriesForNode(
                cache,
                'buildingWithUtilities'
              );
              break;
            }
            case 'pushBartBuildingsWorkflow': {
              // Invalidates facilities
              invalidateAllTopLevelQueriesForNode(cache, 'facilities');
              break;
            }
            case 'processFileUploadWorkflow':
            case 'checkAndGenerateFacilitiesBartsWorkflow':
            case 'transformAndSubmitUploadWorkflow':
            case 'completeOneSchemaSessionForUploadWorkflow': {
              invalidateAllTopLevelQueriesForNode(
                cache,
                'fileImportsByActivityDataType'
              );

              // Invalidates BI queries
              invalidateAllTopLevelQueriesForNode(cache, 'biQueryDataV2');
              break;
            }
            case 'uploadCalculationCustomizationReferenceTableVersionWorkflow': {
              invalidateAllTopLevelQueriesForNode(cache, 'referenceTable');
              invalidateAllTopLevelQueriesForNode(
                cache,
                'calculationCitationsForOrg'
              );
              break;
            }
          }
        },
        cdpSyncWorkflowStatus: (
          result: GQGetCdpWorkflowSyncStatusQuery,
          _args,
          cache,
          _info
        ) => {
          for (const questionSyncStatus of result.cdpSyncWorkflowStatus
            .questions) {
            cache.invalidate('Query', 'cdpSyncQuestionStatusForReport', {
              reportId: questionSyncStatus.reportId,
            });
          }
        },
      },
      Mutation: {
        updateReportingIntentions: (_result, _args, cache, _info) => {
          // Invalidate the cache for the report that was updated
          invalidateAllTopLevelQueriesForNode(cache, 'formalReport');
        },
        completeOnboarding: (_result, _args, cache, _info) => {
          cache.invalidate('Query', 'featureOnboarding');
        },
        archiveReport: (result, _args, cache, _info) => {
          invalidateAllTopLevelQueriesForNode(cache, 'formalReports');
          cache.invalidate('Query', 'climateProgramTimelineProjects');
        },
        bulkStartCompanySurveyEngagement: (_result, _args, cache, _info) => {
          invalidateAllTopLevelQueriesForNode(cache, 'engagementTaskBatches');
          invalidateAllTopLevelQueriesForNode(cache, 'engagementTasks');
        },
        createCdpApiRegistration: (result, _args, cache, _info) => {
          cache.invalidate('Query', 'cdpApiRegistrationForReport');
        },
        deleteCdpApiRegistration: (result, _args, cache, _info) => {
          cache.invalidate('Query', 'cdpApiRegistrationForReport');
        },
        createReportWithConfig: (_result, _args, cache, _info) => {
          invalidateAllTopLevelQueriesForNode(cache, 'formalReports');
          cache.invalidate('Query', 'climateProgramTimelineProjects');
        },
        createReportWithCustomConfig: (_result, _args, cache, _info) => {
          invalidateAllTopLevelQueriesForNode(cache, 'formalReports');
          cache.invalidate('Query', 'climateProgramTimelineProjects');
        },
        startCdpSyncAnswers: (_result, _args, cache, _info) => {
          cache.invalidate('Query', 'cdpLatestSyncWorkflowStatus');
        },
        deleteEngagementTaskConfig(_result, _args, cache, _info) {
          invalidateAllTopLevelQueriesForNode(cache, 'engagementTaskConfigs');
        },
        deleteBiDashboard: (
          result: GQDeleteBiDashboardMutation,
          _args,
          cache,
          _info
        ) => {
          if (!result.deleteBiDashboard?.success) {
            return;
          }

          // Invalidate all biDashboards queries to force a refetch
          // This handles paginated queries with different variables
          cache.invalidate('Query', 'biDashboards');
        },
        deleteDashboard: (
          result: GQDeleteBiDashboardMutation,
          _args,
          cache,
          _info
        ) => {
          if (!result.deleteBiDashboard?.success) {
            return;
          }

          // Invalidate all biDashboards queries to force a refetch
          // This handles paginated queries with different variables
          cache.invalidate('Query', 'biDashboards');
        },
        createPeerCompanyGroup: (_result, _args, cache, _info) => {
          invalidateAllTopLevelQueriesForNode(cache, 'peerCompanyGroups');
        },
        markSupportCaseNotificationRead: (_result, _args, cache) => {
          invalidateAllTopLevelQueriesForNode(
            cache,
            'supportCaseNotificationCount'
          );
        },
        createCustomActivityType: (result, _args, cache, _info) => {
          invalidateAllTopLevelQueriesForNode(cache, 'customActivityTypes');
          cache.updateQuery(
            { query: CustomActivityTypesDocument },
            (data: GQCustomActivityTypesQuery | null) => {
              if (!data?.customActivityTypes) {
                return null;
              }
              const typedResult =
                result.createCustomActivityType as GQCreateCustomActivityTypePayload;
              if (
                typedResult.result.__typename ===
                'CreateCustomActivityTypeSuccessPayload'
              ) {
                data.customActivityTypes.push(
                  typedResult.result.customActivityType
                );
                return data;
              }
              return null;
            }
          );
        },
        createBiDashboard: (
          result: GQCreateBiDashboardMutation,
          _args,
          cache,
          _info
        ) => {
          const newDashboard = result.createBiDashboard?.biDashboard;
          if (!newDashboard) {
            return;
          }

          // Invalidate all biDashboards queries to force a refetch
          // This handles paginated queries with different variables
          cache.invalidate('Query', 'biDashboards');
        },
        createDashboard: (
          result: GQCreateBiDashboardMutation,
          _args,
          cache,
          _info
        ) => {
          const newDashboard = result.createBiDashboard?.biDashboard;
          if (!newDashboard) {
            return;
          }

          // Invalidate all biDashboards queries to force a refetch
          // This handles paginated queries with different variables
          cache.invalidate('Query', 'biDashboards');
        },
        createMeasurementProjectFromWizard: (result, args, cache, info) => {
          // First evict the current measurement project if present
          cache.invalidate('Query', 'activeMeasurementProjects');
          cache.invalidate('Query', 'measurementProjectsRelevantToViewer');
          cache.invalidate('Query', 'climateProgramTimelineProjects');

          // Update the activeOrganization query, so the new measurement project shows up in the sidebar.
          cache.updateQuery(
            { query: ActiveOrganizationDocument, variables: {} },
            (data) => {
              if (data !== null) {
                // @ts-expect-error
                data.activeOrganization.measurementProjects?.push(
                  // @ts-expect-error
                  result.createMeasurementProjectFromWizard.measurementProject
                );
                return data;
              } else {
                return null;
              }
            }
          );
        },
        updateBiDashboard: (
          result: GQUpdateBiDashboardMutation,
          _args,
          cache,
          _info
        ) => {
          const updatedDashboard = result.updateBiDashboard?.biDashboard;
          if (!updatedDashboard) {
            return;
          }

          // Invalidate all biDashboards queries to ensure the list reflects the update
          cache.invalidate('Query', 'biDashboards');
        },
        updateDashboard: (
          result: GQUpdateBiDashboardMutation,
          _args,
          cache,
          _info
        ) => {
          const updatedDashboard = result.updateBiDashboard?.biDashboard;
          if (!updatedDashboard) {
            return;
          }

          // Invalidate all biDashboards queries to ensure the list reflects the update
          cache.invalidate('Query', 'biDashboards');
        },

        createBiDashboardSavedView: (result, args, cache, _info) => {
          const typedResult = result as GQCreateBiDashboardSavedViewMutation;
          const newSavedView =
            typedResult.createBiDashboardSavedView?.savedView;
          if (!newSavedView) {
            return;
          }

          // Update the biDashboard query to include the new saved view
          cache.updateQuery(
            {
              query: GetBiDashboardDocument,
              variables: { id: newSavedView.dashboardId },
            },
            (data: GQGetBiDashboardQuery | null) => {
              if (data?.biDashboard?.savedViews) {
                data.biDashboard.savedViews.push(newSavedView);
              }
              return data;
            }
          );
        },

        updateBiDashboardSavedView: (result, args, cache, _info) => {
          const typedResult = result as GQUpdateBiDashboardSavedViewMutation;
          const updatedSavedView =
            typedResult.updateBiDashboardSavedView?.savedView;
          if (!updatedSavedView) {
            return;
          }

          // Update the saved view in the biDashboard query
          cache.updateQuery(
            {
              query: GetBiDashboardDocument,
              variables: { id: updatedSavedView.dashboardId },
            },
            (data: GQGetBiDashboardQuery | null) => {
              if (data?.biDashboard?.savedViews) {
                const index = data.biDashboard.savedViews.findIndex(
                  (view) => view.id === updatedSavedView.id
                );
                if (index !== -1) {
                  data.biDashboard.savedViews[index] = updatedSavedView;
                }
              }
              return data;
            }
          );
        },

        // We only want to invalidate the activeOrganization query if the name
        // has changed, since this invalidation kinda resets the entire page.
        updateOrg: (
          result: {
            updateOrg: {
              organization: object;
            };
          },
          args,
          cache,
          info
        ) => {
          if (!result.updateOrg) return;

          // Extract updated fields from mutation response
          const updatedOrg = result.updateOrg.organization;

          // @ts-expect-error it isn't typed
          if (updatedOrg.name) {
            // Invalidate `activeOrganization` from the cache
            cache.invalidate({ __typename: 'Query', activeOrganization: null });
          }
        },

        updateOrgProfile: (
          result: GQUpdateOrgProfileMutation,
          args: GQUpdateOrgProfileMutationVariables,
          cache,
          info
        ) => {
          if (
            !info.error &&
            result.updateOrgProfile.orgProfile.completedAt &&
            args.input.isComplete
          ) {
            // Fix for CP-2324. We try to avoid overfetching for forceRedirect but that means
            // we can have a stale value. Specifically, during the onboarding flow we complete
            // onboarding which changes the forceRedirect value for /home from
            // /onboarding/company-metadata to null.
            invalidateAllTopLevelQueriesForNode(cache, 'forceRedirect');
          }
        },
        createClimateProgramProject: (
          result:
            | GQCreateClimateProgramProjectMutation
            | GQCreateClimateProgramProjectForMeasurementMutation,
          args,
          cache,
          info
        ) => {
          // in the new measurement wizard, the next step renders immediately.
          // the data needs to be there to avoid a gimmicky refetch. this will
          // update the cache for all users of the `climateProgramProjects`
          // field on `Query`, so it also works for the homepage.
          const payload =
            result.createClimateProgramProject.climateProgramProject;
          if (payload && 'datasetRequirements' in payload) {
            cache.updateQuery(
              {
                query: NewMeasurementWizardDocument,
              },
              (data: GQNewMeasurementWizardQuery | null) => {
                if (data?.climateProgramProjects) {
                  data.climateProgramProjects.push({
                    ...payload,
                    __typename: 'ClimateProgramProject',
                  });
                }
                return data;
              }
            );
          } else {
            cache.invalidate('Query', 'climateProgramTimelineProjects');
            invalidateAllTopLevelQueriesForNode(
              cache,
              'climateProgramProjects'
            );
          }
        },
        deleteClimateProgramTimelineProject: (result, args, cache, info) => {
          const payload =
            result.deleteClimateProgramTimelineProject as GQDeleteClimateProgramTimelineProjectPayload | null;
          cache.updateQuery(
            {
              query: ClimateProgramProjectsForHomepageDocument,
            },
            (data: GQClimateProgramProjectsForHomepageQuery | null) => {
              if (!data || !data.climateProgramTimelineProjects) {
                return data;
              }
              data.climateProgramTimelineProjects =
                data.climateProgramTimelineProjects.filter(
                  (cptp) => cptp.id !== payload?.id
                );
              return data;
            }
          );
        },
        deleteClimateProgramCalendarEvent: (result, _args, cache, _info) => {
          const payload =
            result.deleteClimateProgramCalendarEvent as GQDeleteClimateProgramCalendarEventPayload | null;
          cache.updateQuery(
            {
              query: ClimateProgramProjectsForHomepageDocument,
            },
            (data: GQClimateProgramProjectsForHomepageQuery | null) => {
              if (!data || !data.climateProgramCalendarEvents) {
                return data;
              }
              data.climateProgramCalendarEvents =
                data.climateProgramCalendarEvents.filter(
                  (cpp) => cpp.id !== payload?.id
                );
              return data;
            }
          );
        },
        createClimateProgramCalendarEvent: (_result, _args, cache, _info) => {
          invalidateAllTopLevelQueriesForNode(
            cache,
            'climateProgramCalendarEvents'
          );
        },
        createRole: (result, args, cache, info) => {
          invalidateAllTopLevelQueriesForNode(cache, 'roles');
        },
        deleteRole: (result, args, cache, info) => {
          cache.invalidate({
            __typename: 'Role',
            id: (args as GQMutationDeleteRoleArgs).input.roleId,
          });
        },
        createOauthClient: (result, args, cache, info) => {
          cache.invalidate('Query', 'oauthClients');
        },
        updateOauthClient: (result, args, cache, info) => {
          cache.invalidate('Query', 'oauthClients');
        },
        deleteOauthClient: (result, args, cache, info) => {
          cache.invalidate({
            __typename: 'ExternalOauthClient',
            id: (args as GQMutationDeleteOauthClientArgs).input.id,
          });
        },
        createApiKey: (result, args, cache, info) => {
          cache.invalidate('Query', 'apiKeys');
        },
        deleteApiKey: (result, args, cache, info) => {
          cache.invalidate({
            __typename: 'ExternalApiKey',
            id: (args as GQMutationDeleteApiKeyArgs).input.id,
          });
        },
        deletePeerCompanyGroup: (result, args, cache, info) => {
          cache.invalidate({
            __typename: 'PeerCompanyGroup',
            id: (args as GQMutationDeletePeerCompanyGroupArgs).input.id,
          });
        },
        createPlanWithDefaults: (result, args, cache, info) => {
          invalidateAllTopLevelQueriesForNode(cache, 'plans');
        },
        grantUserRole: (result, args, cache, info) => {
          cache.invalidate({
            __typename: 'Role',
            id: (args as GQMutationGrantUserRoleArgs).input.roleId,
          });
          // ideally we could just invalidate that single user, but this query doesn't
          // use the userId as an input nor return it as part of the result.
          cache.invalidate('User');
        },
        revokeUserRole: (result, args, cache, info) => {
          cache.invalidate({
            __typename: 'Role',
            id: (args as GQMutationRevokeUserRoleArgs).input.roleId,
          });
          // ideally we could just invalidate that single user, but this query doesn't
          // use the userId as an input nor return it as part of the result.
          cache.invalidate('User');
        },
        bulkGrantUserRole: (result, args, cache, info) => {
          cache.invalidate({
            __typename: 'Role',
            id: (args as GQMutationBulkGrantUserRoleArgs).input.roleId,
          });
          // Since we're modifying multiple users, we need to invalidate all users
          cache.invalidate('User');
        },
        bulkRevokeUserRole: (result, args, cache, info) => {
          cache.invalidate({
            __typename: 'Role',
            id: (args as GQMutationBulkRevokeUserRoleArgs).input.roleId,
          });
          // Since we're modifying multiple users, we need to invalidate all users
          cache.invalidate('User');
        },

        duplicatePlan: (result, args, cache, info) => {
          invalidateAllTopLevelQueriesForNode(cache, 'plans');

          // Ideally, we'd update the activeOrganization.plans query, so the new
          // plan shows up in the sidebar.  But there is an URQL bug where if
          // NODE_ENV=production, calling cache.updateQuery to update the
          // activeOrganization.plans causes it to null out the result.plan.
        },
        deletePlan: (result, args, cache, info) => {
          cache.invalidate({
            __typename: 'Plan',
            id: (args as GQMutationDeletePlanArgs).input.planId,
          });
        },
        createBiSavedView: (result, args, cache, info) => {
          invalidateAllTopLevelQueriesForNode(cache, 'biSavedViewsVersioned');
        },
        createBiSavedViewAndPropagateToCustomReports: (
          result,
          args,
          cache,
          info
        ) => {
          invalidateAllTopLevelQueriesForNode(cache, 'biSavedViews');
          invalidateAllTopLevelQueriesForNode(cache, 'biSavedViewsVersioned');
          invalidateAllTopLevelQueriesForNode(cache, 'reportQuestion');
          invalidateAllTopLevelQueriesForNode(cache, 'formalReport');
        },
        updateBiSavedView: (result, args, cache, info) => {
          invalidateAllTopLevelQueriesForNode(cache, 'biSavedViews');
          invalidateAllTopLevelQueriesForNode(cache, 'biSavedViewsVersioned');
          invalidateAllTopLevelQueriesForNode(cache, 'reportQuestion');
          invalidateAllTopLevelQueriesForNode(cache, 'formalReport');
        },
        restoreDeletedSavedView: (result, args, cache, info) => {
          invalidateAllTopLevelQueriesForNode(cache, 'biSavedViews');
          invalidateAllTopLevelQueriesForNode(cache, 'biSavedViewsVersioned');
          invalidateAllTopLevelQueriesForNode(cache, 'reportQuestion');
          invalidateAllTopLevelQueriesForNode(cache, 'formalReport');
        },
        renameBiSavedView: (result, args, cache, info) => {
          invalidateAllTopLevelQueriesForNode(cache, 'biSavedViews');
          invalidateAllTopLevelQueriesForNode(cache, 'biSavedViewsVersioned');
          invalidateAllTopLevelQueriesForNode(cache, 'reportQuestion');
          invalidateAllTopLevelQueriesForNode(cache, 'formalReport');
        },
        deleteBiSavedView: (result, args, cache, info) => {
          invalidateAllTopLevelQueriesForNode(cache, 'biSavedViews');
          invalidateAllTopLevelQueriesForNode(cache, 'biSavedViewsVersioned');
          invalidateAllTopLevelQueriesForNode(cache, 'reportQuestion');
          invalidateAllTopLevelQueriesForNode(cache, 'formalReport');
        },
        createBiCustomMetric: (result, args, cache, info) => {
          invalidateAllTopLevelQueriesForNode(cache, 'biCustomMetrics');
        },
        updateBiCustomMetric: (result, args, cache, info) => {
          invalidateAllTopLevelQueriesForNode(cache, 'biCustomMetrics');
        },
        deleteBiCustomMetric: (result, args, cache, info) => {
          invalidateAllTopLevelQueriesForNode(cache, 'biCustomMetrics');
        },
        upsertReportAnswerWithMetadata: (_result, args, cache, _info) => {
          const { reportId, outputJson, oldOutputJson } =
            args.input as GQUpsertReportAnswerInput;

          // When a report answer changes, any AI analysis for that answer
          // becomes stale. Invalidate all ReportAnswerAiAnalysis entities
          // to force refetching (including the isStale field).
          cache.invalidate('ReportAnswerAiAnalysis');

          // If the materiality assessment linked to this report has changed,
          // then we need to make sure the materiality status is up to date for
          // all questions in the report.
          //
          // We use optional chaining here because both outputJson and
          // oldOutputJson could be undefined, but they're typed as any ):
          if (
            outputJson?.materialityAssessmentId ||
            oldOutputJson?.materialityAssessmentId
          ) {
            // Ideally we would just invalidate the ReportQuestions or even
            // better, the materiality for those report questions. However, that
            // does not cause the page to re-fetch today.
            cache.invalidate('Query', 'formalReport', { id: reportId });
            // To make sure the materiality index has an up to date list.
            cache.invalidate('Query', 'allMaterialityAssessments');
          }
        },
        createReportFileAttachments: (result, args, cache, info) => {
          invalidateAllTopLevelQueriesForNode(cache, 'reportAttachmentItems');
        },
        createReportURLAttachmentItem: (result, args, cache, info) => {
          invalidateAllTopLevelQueriesForNode(cache, 'reportAttachmentItems');
        },
        deleteReportAttachmentItem: (result, args, cache, info) => {
          invalidateAllTopLevelQueriesForNode(cache, 'reportAttachmentItems');
        },
        deleteReportAttachment: (result, args, cache, info) => {
          cache.invalidate('ReportQuestionInstance');
        },
        createReportAttachments: (result, args, cache, info) => {
          cache.invalidate('ReportQuestionInstance');
          invalidateAllTopLevelQueriesForNode(cache, 'reportAttachmentItems');
        },
        // TODO: After we move createUser() mutation to TS, we can add back the
        // `org { users { ... } }` return node, which will allow urql to update
        // the users cache itself.
        createUser(result, args, cache, info) {
          const payload = result.createUser as GQCreateUserPayload | null;

          if (payload) {
            cache.updateQuery(
              {
                query: GetAllAssignableUsersDocument,
              },
              (data) => {
                if (data !== null) {
                  (
                    data as GQGetAllAssignableUsersQuery
                  ).organization?.users?.edges.push({
                    __typename: 'UserEdge',
                    node: payload.user,
                  });
                  return data;
                }
                return null;
              }
            );
          }
        },

        createFootprint: (result, args, cache, info) => {
          const payload =
            result.createFootprint as GQCreateFootprintPayload | null;
          if (!payload) return;

          cache.updateQuery(
            { query: FootprintsForFootprintListDocument },
            (data: GQFootprintsForFootprintListQuery | null) => {
              if (data === null) return null;

              data.footprints.edges.unshift({
                __typename: 'FootprintEdge',
                node: payload.footprint,
              });

              return data;
            }
          );
        },

        renameFootprint: (result, args, cache, info) => {
          const payload = result.renameFootprint as GQRenameFootprintPayload;
          if (!payload) return;

          // Update list of all footprints query
          cache.updateQuery(
            { query: FootprintsForFootprintListDocument },
            (data: GQFootprintsForFootprintListQuery | null) => {
              const existingRecord = data?.footprints.edges.find(
                (record) => record?.node?.id === payload.footprint.id
              );
              if (!existingRecord?.node) {
                return null;
              }
              existingRecord.node.name = payload.footprint.name;
              return data;
            }
          );

          // Also update singular footprint query for the right id
          invalidateAllTopLevelQueriesForNode(cache, 'footprint');

          // ...and update footprint detail page fragment's top level call that has a
          // footprint embedded in it since we don't cache Footprint objects
          // directly... we probably should :grimace:
          invalidateAllTopLevelQueriesForNode(cache, 'footprintAnalysis');
        },

        archiveFootprint: (result, args, cache, info) => {
          const payload = result.archiveFootprint as GQArchiveFootprintPayload;
          if (!payload) return;

          cache.updateQuery(
            { query: FootprintsForFootprintListDocument },
            (data: GQFootprintsForFootprintListQuery | null) => {
              if (data === null) return null;

              data.footprints.edges = data.footprints.edges.filter(
                (record) => record?.node?.id !== payload.footprint.id
              );

              return data;
            }
          );

          cache.updateQuery(
            { query: FootprintsForArchivedFootprintListDocument },
            (data: GQFootprintsForArchivedFootprintListQuery | null) => {
              if (data === null) return null;

              // Only add the footprint if it's not already in the list
              const existingFootprint = data.footprints.edges.find(
                (record) => record?.node?.id === payload.footprint.id
              );
              if (!existingFootprint) {
                data.footprints.edges.push({
                  __typename: 'FootprintEdge',
                  node: payload.footprint,
                });
              }

              return data;
            }
          );

          invalidateAllTopLevelQueriesForNode(cache, 'footprint');
        },

        unarchiveFootprint: (result, args, cache, info) => {
          const payload =
            result.unarchiveFootprint as GQUnarchiveFootprintPayload;
          if (!payload) return;

          // Add the footprint to the list of unarchived footprints
          cache.updateQuery(
            { query: FootprintsForFootprintListDocument },
            (data: GQFootprintsForFootprintListQuery | null) => {
              if (data === null) return null;

              // Only add the footprint if it's not already in the list
              const existingFootprint = data.footprints.edges.find(
                (record) => record?.node?.id === payload.footprint.id
              );
              if (!existingFootprint) {
                data.footprints.edges.push({
                  __typename: 'FootprintEdge',
                  node: payload.footprint,
                });
              }

              return data;
            }
          );

          // Remove the footprint from the archived list
          cache.updateQuery(
            { query: FootprintsForArchivedFootprintListDocument },
            (data: GQFootprintsForArchivedFootprintListQuery | null) => {
              if (data === null) return null;

              data.footprints.edges = data.footprints.edges.filter(
                (record) => record?.node?.id !== payload.footprint.id
              );

              return data;
            }
          );

          invalidateAllTopLevelQueriesForNode(cache, 'footprint');
        },

        ///////////////////////////////////////////////////////////////////////
        // Win Finance (start)
        ///////////////////////////////////////////////////////////////////////
        createSnapshot: (result, args, cache, info) => {
          cache.updateQuery(
            { query: FinanceSnapshotsDocument, variables: {} },
            (data: GQFinanceSnapshotsQuery | null) => {
              // @ts-expect-error
              if (data !== null && result && result.createSnapshot?.snapshot) {
                data.financeSnapshots?.push(
                  // @ts-expect-error
                  result.createSnapshot.snapshot
                );
                return data;
              } else {
                return null;
              }
            }
          );
        },
        deleteSnapshot: (
          result: GQDeleteSnapshotMutation,
          args,
          cache,
          info
        ) => {
          cache.updateQuery(
            { query: FinanceSnapshotsDocument, variables: {} },
            (data: GQFinanceSnapshotsQuery | null) => {
              if (data !== null && result && result.deleteSnapshot?.id) {
                const existingRecords = data.financeSnapshots;
                if (!existingRecords || !Array.isArray(existingRecords))
                  return null;
                const deletedRecordId = result.deleteSnapshot.id;
                if (deletedRecordId) {
                  const index = existingRecords.findIndex(
                    (record) => record.id === deletedRecordId
                  );
                  if (index !== -1) {
                    existingRecords.splice(index, 1);
                  }
                }
                return data;
              } else {
                return null;
              }
            }
          );
        },
        createFund: (result: GQCreateFundMutation, args: any, cache, _info) => {
          const payload = result.createFund;

          if (payload) {
            cache.updateQuery({ query: FundList }, (data) => {
              const existingRecords = data?.funds;
              if (!existingRecords || !Array.isArray(existingRecords))
                return null;
              const newRecord = payload.fund;
              if (newRecord) {
                existingRecords.push(newRecord);
              }
              return data;
            });

            cache.updateQuery(
              {
                query: FinanceSettingsDocument,
                variables: {},
              },
              (data) => {
                if (data == null) return null;
                (data as GQFinanceSettingsQuery).funds?.push(payload.fund);
                return data;
              }
            );
          }
        },
        deleteFootprintExport: (result, args, cache, info) => {
          invalidateAllTopLevelQueriesForNode(cache, 'footprintExports');
        },
        deleteFunds: (
          result: { deleteFunds: GQDeleteFundsPayload },
          args: any,
          cache,
          _info
        ) => {
          invalidateAllTopLevelQueriesForNode(
            cache,
            'financeModelCardMetadata'
          );
          const { ids } = result.deleteFunds;
          cache.updateQuery({ query: FundList }, (data) => {
            if (!data?.funds || !Array.isArray(data.funds)) return null;
            data.funds = data.funds.filter((fund) => !ids.includes(fund.id));
            return data;
          });
          cache.updateQuery(
            {
              query: FinanceSettingsDocument,
              variables: {},
            },
            (data) => {
              if (data == null) return null;
              data.funds = (data as GQFinanceSettingsQuery).funds.filter(
                (fund) => !ids.includes(fund.id)
              );
              return data;
            }
          );
        },
        createAssetCorporate: (
          result: GQCreateAssetCorporateMutation,
          _args: any,
          cache,
          info
        ) => {
          invalidateAllTopLevelQueriesForNode(
            cache,
            'financeModelCardMetadata'
          );
          if (info.error) return;
          cache.updateQuery({ query: AssetCorporateList }, (data) => {
            const existingRecords = data?.assetsCorporate;
            if (!existingRecords || !Array.isArray(existingRecords))
              return null;
            const newAsset = result.createAssetCorporate?.asset;
            if (newAsset) {
              existingRecords.push(newAsset);
            }
            return data;
          });

          if (result.createAssetCorporate?.asset?.assetGroupId) {
            cache.invalidate({
              __typename: 'AssetGroup',
              id: result.createAssetCorporate.asset.assetGroupId,
            });
          }
        },
        updateAssetCorporate: (result: any, args: any, cache, info) => {
          invalidateAllTopLevelQueriesForNode(cache, 'financeChangelog');
        },
        createAssetRealEstate: (
          result: GQCreateAssetRealEstateMutation,
          args: any,
          cache,
          info
        ) => {
          invalidateAllTopLevelQueriesForNode(
            cache,
            'financeModelCardMetadata'
          );
          if (info.error) return;
          cache.updateQuery({ query: AssetRealEstateList }, (data) => {
            const existingRecords = data?.assetsRealEstate;
            if (!existingRecords || !Array.isArray(existingRecords))
              return null;
            const newAsset = result.createAssetRealEstate?.asset;
            if (newAsset) {
              existingRecords.push(newAsset);
            }
            return data;
          });
          if (result.createAssetRealEstate?.asset?.assetGroupId) {
            cache.invalidate({
              __typename: 'AssetGroup',
              id: result.createAssetRealEstate.asset.assetGroupId,
            });
          }
        },
        updateAssetRealEstate: (result: any, args: any, cache, info) => {
          if (info.error) return;
          invalidateAllTopLevelQueriesForNode(cache, 'financeChangelog');
        },
        createAssetGroup: (
          result: GQCreateAssetGroupMutation,
          _args: unknown,
          cache,
          info
        ) => {
          invalidateAllTopLevelQueriesForNode(
            cache,
            'financeModelCardMetadata'
          );
          if (info.error) return;
          cache.updateQuery({ query: AssetGroupList }, (data) => {
            const existingRecords = data?.assetGroups;
            if (!existingRecords || !Array.isArray(existingRecords))
              return null;
            const newAsset = result.createAssetGroup?.asset;
            if (newAsset) {
              existingRecords.push(newAsset);
            }
            return data;
          });
        },
        updateAssetGroup: (result: any, args: any, cache, info) => {
          if (info.error) return;
          invalidateAllTopLevelQueriesForNode(cache, 'financeChangelog');
        },
        createAssetSovereignBond: (
          result: GQCreateAssetSovereignBondMutation,
          _args,
          cache,
          info
        ) => {
          invalidateAllTopLevelQueriesForNode(
            cache,
            'financeModelCardMetadata'
          );
          if (info.error) return;
          cache.updateQuery({ query: AssetSovereignBondList }, (data) => {
            const existingRecords = data?.assetSovereignBond;
            if (!existingRecords || !Array.isArray(existingRecords))
              return null;
            const newAsset = result.createAssetSovereignBond?.asset;
            if (newAsset) {
              existingRecords.push(newAsset);
            }
            return data;
          });
        },
        updateAssetSovereignBond: (result: any, args: any, cache, info) => {
          if (info.error) return;
          invalidateAllTopLevelQueriesForNode(cache, 'financeChangelog');
        },
        createAssetPersonalMotorVehicleInsurance: (
          result: GQCreateAssetPersonalMotorVehicleInsuranceMutation,
          _args,
          cache,
          info
        ) => {
          invalidateAllTopLevelQueriesForNode(
            cache,
            'financeModelCardMetadata'
          );
          if (info.error) return;
          cache.updateQuery(
            { query: AssetPersonalMotorVehicleInsuranceList },
            (data) => {
              const existingRecords = data?.assetPersonalMotorVehicleInsurance;
              if (!existingRecords || !Array.isArray(existingRecords))
                return null;
              const newAsset =
                result.createAssetPersonalMotorVehicleInsurance?.asset;
              if (newAsset) {
                existingRecords.push(newAsset);
              }
              return data;
            }
          );
        },
        updateAssetPersonalMotorVehicleInsurance: (
          result: any,
          args: any,
          cache,
          info
        ) => {
          if (info.error) return;
          invalidateAllTopLevelQueriesForNode(cache, 'financeChangelog');
        },
        deleteAssets(result: GQDeleteAssetsMutation, _args, cache, _info) {
          invalidateAllTopLevelQueriesForNode(
            cache,
            'financeModelCardMetadata'
          );
          invalidateAllTopLevelQueriesForNode(cache, 'funds');
          invalidateAllTopLevelQueriesForNode(cache, 'assetsCorporate');
          invalidateAllTopLevelQueriesForNode(cache, 'assetsRealEstate');
          invalidateAllTopLevelQueriesForNode(cache, 'assetGroups');
          invalidateAllTopLevelQueriesForNode(cache, 'assetSovereignBonds');
          invalidateAllTopLevelQueriesForNode(
            cache,
            'assetPersonalMotorVehicleInsurances'
          );
          invalidateAllTopLevelQueriesForNode(cache, 'assetYears');
          invalidateAllTopLevelQueriesForNode(cache, 'sovereignBondAssetYears');
          invalidateAllTopLevelQueriesForNode(
            cache,
            'personalMotorVehicleInsuranceAssetYears'
          );
          invalidateAllTopLevelQueriesForNode(cache, 'assetHoldings');
          invalidateAllTopLevelQueriesForNode(
            cache,
            'personalMotorVehicleInsuranceAssetHoldings'
          );
          invalidateAllTopLevelQueriesForNode(cache, 'contacts');
          invalidateAllTopLevelQueriesForNode(cache, 'financeInputDataFlags');
        },
        createFinanceTag: (
          result: GQCreateFinanceTagMutation,
          args: GQCreateFinanceTagMutationVariables,
          cache,
          info
        ) => {
          if (info.error) return;
          invalidateAllTopLevelQueriesForNode(cache, 'financeChangelog');

          const FinanceTagList = gql`
            query FinanceTagList @withOwner(owner: WinFinance) {
              financeTags {
                id
              }
            }
          `;
          cache.updateQuery({ query: FinanceTagList }, (data) => {
            const existingRecords = data?.financeTags;
            if (!existingRecords || !Array.isArray(existingRecords))
              return null;
            const newRecord = result.createFinanceTag?.financeTag;
            if (newRecord) {
              existingRecords.push(newRecord);
            }
            return data;
          });
        },
        deleteFinanceTag: (
          result: GQDeleteFinanceTagMutation,
          args: GQDeleteFinanceTagMutationVariables,
          cache,
          info
        ) => {
          if (info.error) return;
          const FinanceTagList = gql`
            query FinanceTagList @withOwner(owner: WinFinance) {
              financeTags {
                id
              }
            }
          `;
          cache.updateQuery({ query: FinanceTagList }, (data) => {
            const existingRecords = data?.financeTags;
            if (!existingRecords || !Array.isArray(existingRecords))
              return null;
            const deletedRecordId = result.deleteFinanceTag.id;
            if (deletedRecordId) {
              const index = existingRecords.findIndex(
                (record) => record.id === deletedRecordId
              );
              if (index !== -1) {
                existingRecords.splice(index, 1);
              }
            }
            return data;
          });
        },
        deleteFinanceTags: (
          result: GQDeleteFinanceTagsMutation,
          args: GQDeleteFinanceTagsMutationVariables,
          cache,
          info
        ) => {
          if (info.error) return;
          invalidateDeletedRecords(
            cache,
            result.deleteFinanceTags.ids,
            'FinanceTag'
          );
        },
        createFinanceReportingTags: (
          result: GQCreateFinanceReportingTagsMutation,
          args: GQCreateFinanceReportingTagsMutationVariables,
          cache,
          info
        ) => {
          if (info.error) return;
          invalidateAllTopLevelQueriesForNode(cache, 'financeChangelog');

          const FinanceTagList = gql`
            query FinanceTagList @withOwner(owner: WinFinance) {
              financeTags {
                id
              }
            }
          `;
          cache.updateQuery({ query: FinanceTagList }, (data) => {
            const existingRecords = data?.financeTags;
            if (!existingRecords || !Array.isArray(existingRecords))
              return null;
            const newRecords = result.createFinanceReportingTags?.financeTags;
            if (newRecords) {
              existingRecords.push(...newRecords);
            }
            return data;
          });
        },
        createAssetYear: (result: any, args: any, cache, info) => {
          invalidateAllTopLevelQueriesForNode(
            cache,
            'financeModelCardMetadata'
          );
          if (info.error) return;
          invalidateAllTopLevelQueriesForNode(cache, 'financeChangelog');
        },
        updateAssetYear: (result: any, args: any, cache, info) => {
          if (info.error) return;
          invalidateAllTopLevelQueriesForNode(cache, 'financeChangelog');
        },
        createAssetHolding: (
          result: GQCreateAssetHoldingMutation,
          args: GQCreateAssetHoldingMutationVariables,
          cache,
          info
        ) => {
          invalidateAllTopLevelQueriesForNode(
            cache,
            'financeModelCardMetadata'
          );
          if (info.error) return;
          invalidateAllTopLevelQueriesForNode(cache, 'financeChangelog');

          const AssetHoldingList = gql`
            query AssetHoldingList @withOwner(owner: WinFinance) {
              assetHoldings {
                id
              }
            }
          `;
          cache.updateQuery({ query: AssetHoldingList }, (data) => {
            const existingRecords = data?.assetHoldings;
            if (!existingRecords || !Array.isArray(existingRecords))
              return null;
            const newRecord = result.createAssetHolding?.assetHolding;
            if (newRecord) {
              existingRecords.push(newRecord);
            }
            return data;
          });
        },
        updateAssetHolding: (result: any, args: any, cache, info) => {
          if (info.error) return;
          invalidateAllTopLevelQueriesForNode(cache, 'financeChangelog');
        },
        deleteAssetHolding: (
          result: GQDeleteAssetHoldingMutation,
          args: GQDeleteAssetHoldingMutationVariables,
          cache,
          info
        ) => {
          invalidateAllTopLevelQueriesForNode(
            cache,
            'financeModelCardMetadata'
          );
          if (info.error) return;
          const AssetHoldingList = gql`
            query AssetHoldingList @withOwner(owner: WinFinance) {
              assetHoldings {
                id
              }
            }
          `;
          cache.updateQuery({ query: AssetHoldingList }, (data) => {
            const existingRecords = data?.assetHoldings;
            if (!existingRecords || !Array.isArray(existingRecords))
              return null;
            const deletedRecordId = result.deleteAssetHolding?.id;
            if (deletedRecordId) {
              const index = existingRecords.findIndex(
                (record) => record.id === deletedRecordId
              );
              if (index !== -1) {
                existingRecords.splice(index, 1);
              }
            }
            return data;
          });
          const deletedFinanceDataFlagIds =
            result.deleteAssetHolding?.deletedFinanceDataFlagIds;
          if (deletedFinanceDataFlagIds) {
            invalidateDeletedRecords(
              cache,
              deletedFinanceDataFlagIds,
              'FinanceInputDataFlag'
            );
          }
        },

        updateFinanceAutomaticReminders: (
          result: GQUpdateFinanceAutomaticRemindersMutation,
          args: any,
          cache
        ) => {
          // not ideal, i know
          invalidateAllTopLevelQueriesForNode(
            cache,
            'financeAutomaticReminders'
          );
        },
        ///////////////////////////////////////////////////////////////////////
        // Win Finance (end)
        ///////////////////////////////////////////////////////////////////////

        createSupplierScoreCriteria: (
          result: GQCreateSupplierScoreCriteriaMutation,
          args,
          cache
        ) => {
          cache.updateQuery(
            { query: SupplierScorecardsDocument },
            (data?: GQSupplierScorecardsQuery | null) => {
              if (!data) {
                return data;
              }

              const newCriteria =
                result.createSupplierScoreCriteria.supplierScoreCriteria;

              const scorecardIndex = data.supplierScorecards.findIndex(
                (scorecard) => scorecard.id === newCriteria.supplierScoreId
              );
              if (scorecardIndex === -1) {
                return data;
              }

              data.supplierScorecards[scorecardIndex].criteria.push(
                newCriteria
              );
              return data;
            }
          );
        },

        upsertOrgCompanyForName: (
          result: GQUpsertOrgCompanyForNameMutation,
          args: GQUpsertOrgCompanyForNameMutationVariables,
          cache,
          info
        ) => {
          const Organization = gql`
            # eslint-disable-next-line @watershed/gql-operations-must-assign-owners
            query OrganizationForCacheUpdate {
              organization {
                id
                orgCompanies {
                  id
                  name
                }
              }
            }
          `;

          cache.updateQuery({ query: Organization }, (data) => {
            const org = data?.organization as any;
            const orgCompanies = org?.orgCompanies;
            if (!org || !orgCompanies || !Array.isArray(orgCompanies)) {
              return null;
            }
            const newRecord = result.upsertOrgCompanyForName?.orgCompany;
            if (newRecord) {
              // remove record from orgCompanies inplace
              const index = orgCompanies.findIndex(
                (record) => record.id === newRecord.id
              );
              if (index !== -1) {
                orgCompanies.splice(index, 1);
              }

              orgCompanies.push(newRecord);
            }
            return data;
          });
          invalidateAllTopLevelQueriesForNode(cache, 'suppliersV2');
          invalidateAllTopLevelQueriesForNode(
            cache,
            'preMeasurementSupplierMappingV1Analytics'
          );

          updateCachedSupplierMappingRows(
            cache,
            CachedSupplierMappingTableRowAction.UpdateOrgCompany,
            result
          );
        },

        upsertNonFootprintSupplier: (
          _result: GQUpsertNonFootprintSupplierMutation,
          _args: GQUpsertNonFootprintSupplierMutationVariables,
          cache
        ) => {
          invalidateAllTopLevelQueriesForNode(cache, 'suppliersV2');
        },
        deleteNonFootprintSupplier: (
          _result: GQDeleteNonFootprintSupplierMutation,
          _args: GQDeleteNonFootprintSupplierMutationVariables,
          cache
        ) => {
          invalidateAllTopLevelQueriesForNode(cache, 'suppliersV2');
        },
        deleteOrgCompany: (
          result: GQDeleteOrgCompanyMutation,
          args: GQDeleteOrgCompanyMutationVariables,
          cache
        ) => {
          const Organization = gql`
            # eslint-disable-next-line @watershed/gql-operations-must-assign-owners
            query OrganizationForDeleteOrgCompanyCacheUpdate {
              organization {
                id
                orgCompanies {
                  id
                  name
                }
              }
            }
          `;

          cache.updateQuery({ query: Organization }, (data) => {
            const org = data?.organization as any;
            const orgCompanies = org?.orgCompanies;
            if (!org || !orgCompanies || !Array.isArray(orgCompanies)) {
              return null;
            }
            const deletedRecordId = result.deleteOrgCompany?.id;
            if (deletedRecordId) {
              // remove record from orgCompanies inplace
              const index = orgCompanies.findIndex(
                (record) => record.id === deletedRecordId
              );
              if (index !== -1) {
                orgCompanies.splice(index, 1);
              }
            }
            return data;
          });

          updateCachedSupplierMappingRows(
            cache,
            CachedSupplierMappingTableRowAction.DeleteOrgCompany,
            result
          );
        },

        addEngagementTaskComment: (
          result: GQAddEngagementTaskCommentMutation,
          args: GQAddEngagementTaskCommentMutationVariables,
          cache,
          _info
        ) => {
          const comment = result.addEngagementTaskComment?.comment;
          if (!comment) {
            return;
          }

          patchFragmentInCache({
            cache,
            fragment: EngagementTaskContentsFragmentDoc,
            maybeIdToUpdate: args.engagementTaskId,
            patch: (engagementTask) => ({
              ...engagementTask,
              comments: [...engagementTask.comments, comment],
            }),
          });
        },
        updateSupplierCustomData: (
          result: GQUpdateSupplierCustomDataMutation,
          _args: GQUpdateSupplierCustomDataMutationVariables,
          cache,
          _info
        ) => {
          const newCustomData =
            result.updateSupplierCustomData?.supplierCustomData;
          if (!newCustomData) {
            return;
          }
          patchSupplierInCache(
            cache,
            result.updateSupplierCustomData?.supplierId,
            (gqlSupplier) => ({
              ...gqlSupplier,
              notes: newCustomData.notes,
              customDataV2: {
                ...gqlSupplier.customDataV2,
                ...newCustomData.customData,
              },
            }),
            (supplier) => ({
              ...supplier,
              priority: newCustomData.priority ?? supplier.priority,
              notes: newCustomData.notes ?? supplier.notes,
              customDataV2: {
                ...supplier.customDataV2,
                ...newCustomData.customData,
              },
            })
          );
        },
        createSupplierSavedView: (
          { createSupplierSavedView }: GQCreateSupplierSavedViewMutation,
          args: GQCreateSupplierSavedViewMutationVariables,
          cache,
          _info
        ) => {
          cache.updateQuery(
            { query: GetSupplierViewsDocument },
            (data: GQGetSupplierViewsQuery | null) => {
              data?.supplierViews?.push(createSupplierSavedView.view);
              return data;
            }
          );
        },
        updateSupplierSavedView: (
          { updateSupplierSavedView }: GQUpdateSupplierSavedViewMutation,
          args: GQUpdateSupplierSavedViewMutationVariables,
          cache,
          _info
        ) => {
          cache.updateQuery(
            { query: GetSupplierViewsDocument },
            (data: GQGetSupplierViewsQuery | null) => {
              if (updateSupplierSavedView.view) {
                const index =
                  data?.supplierViews?.findIndex(
                    (view) => view.id === updateSupplierSavedView.view.id
                  ) ?? -1;
                if (index !== -1) {
                  data?.supplierViews?.splice(index, 1);
                }

                data?.supplierViews?.push(updateSupplierSavedView.view);
              }
              return data;
            }
          );
        },
        deleteSupplierSavedView: (
          { deleteSupplierSavedView }: GQDeleteSupplierSavedViewMutation,
          args: GQDeleteSupplierSavedViewMutationVariables,
          cache,
          _info
        ) => {
          cache.updateQuery(
            { query: GetSupplierViewsDocument },
            (data: GQGetSupplierViewsQuery | null) => {
              if (data?.supplierViews === undefined) {
                return data;
              }
              data.supplierViews = data.supplierViews?.filter(
                (view) => view.id !== deleteSupplierSavedView.id
              );
              return data;
            }
          );
        },
        createSupplierContact: (
          result: GQCreateSupplierContactMutation,
          args: GQCreateSupplierContactMutationVariables,
          cache,
          _info
        ) => {
          const newContact = result.createSupplierContact?.contact;
          if (!newContact) {
            return;
          }
          cache.updateQuery(
            {
              query: GetContactsForOrgDocument,
            },
            (data: GQGetContactsForOrgQuery | null) => {
              data?.contacts.push({
                ...newContact,
                companyId: args.input.companyId,
              });
              return data;
            }
          );
          cache.updateQuery(
            {
              query: GetContactsForCompanyDocument,
              variables: { companyId: args.input.companyId },
            },
            (data: GQGetContactsForCompanyQuery | null) => {
              data?.contactsForCompany.push({
                ...newContact,
                companyId: args.input.companyId,
              });
              return data;
            }
          );
        },
        updateSupplierContact: (
          result: GQUpdateSupplierContactMutation,
          args: GQUpdateSupplierContactMutationVariables,
          cache,
          _info
        ) => {
          const newContact = result.updateSupplierContact?.contact;
          if (!newContact) {
            return;
          }
          cache.updateQuery(
            {
              query: GetContactsForOrgDocument,
            },
            (data: GQGetContactsForOrgQuery | null) => {
              if (isNullish(data?.contacts)) {
                return data;
              }

              data.contacts = data.contacts.map((contact) =>
                contact.id === newContact.id ? newContact : contact
              );
              return data;
            }
          );

          cache.updateQuery(
            {
              query: GetContactsForCompanyDocument,
              variables: { companyId: newContact.companyId },
            },
            (data: GQGetContactsForCompanyQuery | null) => {
              if (isNullish(data?.contactsForCompany)) {
                return data;
              }

              data.contactsForCompany = data.contactsForCompany.map(
                (contact) =>
                  contact.id === newContact.id ? newContact : contact
              );
              return data;
            }
          );
        },
        deleteSupplierContact: (
          _result: GQDeleteSupplierContactMutation,
          args: GQDeleteSupplierContactMutationVariables,
          cache,
          info
        ) => {
          cache.updateQuery(
            {
              query: GetContactsForOrgDocument,
            },
            (data: GQGetContactsForOrgQuery | null) => {
              if (data?.contacts === undefined) {
                return data;
              }
              data.contacts = data?.contacts.filter(
                (contact) => contact.id !== args.input.id
              );
              return data;
            }
          );
          // Can remove this if statement once we make companyId required
          if (isNotNullish(args.input.companyId)) {
            cache.updateQuery(
              {
                query: GetContactsForCompanyDocument,
                variables: { companyId: args.input.companyId },
              },
              (data: GQGetContactsForCompanyQuery | null) => {
                if (data?.contactsForCompany === undefined) {
                  return data;
                }
                data.contactsForCompany = data?.contactsForCompany.filter(
                  (contact) => contact.id !== args.input.id
                );
                return data;
              }
            );
          }
        },
        createSupplierAttachments: (
          result: GQCreateSupplierAttachmentsMutation,
          args: GQCreateSupplierAttachmentsMutationVariables,
          cache,
          info
        ) => {
          cache.updateQuery(
            {
              query: GetSupplierAttachmentsDocument,
              variables: { companyId: args.input.companyId },
            },
            (data: GQGetSupplierAttachmentsQuery | null) => {
              data?.supplierAttachments.push(
                ...(result.createSupplierAttachments?.fileUrls.map(
                  (fileUrl) => fileUrl.attachment
                ) ?? [])
              );
              return data;
            }
          );
        },
        deleteSupplierAttachment: (
          _result: GQDeleteSupplierAttachmentMutation,
          args: GQDeleteSupplierAttachmentMutationVariables,
          cache,
          info
        ) => {
          cache.updateQuery(
            {
              query: GetSupplierAttachmentsDocument,
              variables: { companyId: args.input.companyId },
            },
            (data: GQGetSupplierAttachmentsQuery | null) => {
              if (data?.supplierAttachments === undefined) {
                return data;
              }
              data.supplierAttachments = data?.supplierAttachments.filter(
                (attachment) =>
                  attachment.fileMetadataId !== args.input.fileMetadataId
              );
              return data;
            }
          );
        },
        deleteSupplierTableColumn: (
          { deleteSupplierTableColumn }: GQDeleteSupplierTableColumnMutation,
          args: GQDeleteSupplierTableColumnMutationVariables,
          cache,
          _info
        ) => {
          cache.updateQuery(
            {
              query: GetSupplierColumnsForCustomFieldDocument,
            },
            (data: GQGetSupplierColumnsForCustomFieldQuery | null) => {
              if (data?.supplierTableColumnsDashboard === undefined) {
                return data;
              }
              data.supplierTableColumnsDashboard =
                data.supplierTableColumnsDashboard?.filter(
                  (column) => column.id !== args.input.id
                );
              return data;
            }
          );
        },
        createEngagementCohort: (
          { createEngagementCohort }: GQCreateEngagementCohortMutation,
          args: GQCreateEngagementCohortMutationVariables,
          cache,
          _info
        ) => {
          cache.updateQuery(
            { query: GetEngagementCohortsDocument },
            (data: GQGetEngagementCohortsQuery | null) => {
              data?.engagementCohorts?.push(createEngagementCohort);
              return data;
            }
          );
        },

        createEngagementCohortsForCompany: (
          {
            createEngagementCohortsForCompany: newCohorts,
          }: GQCreateEngagementCohortsForCompanyMutation,
          {
            input: { companyId },
          }: GQCreateEngagementCohortsForCompanyMutationVariables,
          cache,
          _info
        ) => {
          cache.updateQuery(
            { query: GetEngagementCohortsDocument },
            (data: GQGetEngagementCohortsQuery | null) => {
              data?.engagementCohorts?.push(...newCohorts);
              return data;
            }
          );
          patchSupplierInCache(
            cache,
            createSupplierId({ companyId }),
            (gqlSupplier) => gqlSupplier,
            (supplier) => ({
              ...supplier,
              engagementCohortIds: [
                ...supplier.engagementCohortIds,
                ...newCohorts.map((cohort) => cohort.id),
              ],
            })
          );
        },
        setEngagementCohortsForCompany: (
          {
            setEngagementCohortsForCompany,
          }: GQSetEngagementCohortsForCompanyMutation,
          args,
          cache,
          info
        ) => {
          const { companyId, cohortIds } = setEngagementCohortsForCompany;
          patchSupplierInCache(
            cache,
            createSupplierId({ companyId }),
            (gqlSupplier) => gqlSupplier,
            (supplier) => ({
              ...supplier,
              engagementCohortIds: cohortIds,
            })
          );
        },
        setEngagementCohortAndMembers: (
          {
            setEngagementCohortAndMembers,
          }: GQSetEngagementCohortAndMembersMutation,
          args,
          cache,
          info
        ) => {
          const { oldCompanyIds, engagementCohortWithCompanyIds } =
            setEngagementCohortAndMembers;

          oldCompanyIds.forEach((companyId) =>
            patchSupplierInCache(
              cache,
              createSupplierId({ companyId }),
              (gqlSupplier) => gqlSupplier,
              (supplier) => ({
                ...supplier,
                engagementCohortIds: supplier.engagementCohortIds.filter(
                  (cohortId) => cohortId !== engagementCohortWithCompanyIds.id
                ),
              })
            )
          );

          engagementCohortWithCompanyIds.companyIds?.forEach((companyId) =>
            patchSupplierInCache(
              cache,
              createSupplierId({ companyId }),
              (gqlSupplier) => gqlSupplier,
              (supplier) => ({
                ...supplier,
                engagementCohortIds: [
                  engagementCohortWithCompanyIds.id,
                  ...supplier.engagementCohortIds.filter(
                    (cohortId) => cohortId !== engagementCohortWithCompanyIds.id
                  ),
                ],
              })
            )
          );

          cache.updateQuery(
            { query: GetEngagementCohortsDocument },
            (data: GQGetEngagementCohortsQuery | null) => {
              if (isNullish(data)) {
                return null;
              }
              data.engagementCohorts = data.engagementCohorts.map((cohort) => {
                if (cohort.id !== engagementCohortWithCompanyIds.id) {
                  return cohort;
                }
                return {
                  ...cohort,
                  name: engagementCohortWithCompanyIds.name,
                };
              });
              return data;
            }
          );
        },
        deleteCompanyEngagementCohort: (
          {
            deleteCompanyEngagementCohort: { affectedCompanyIds },
          }: GQDeleteCompanyEngagementCohortMutation,
          args: GQDeleteCompanyEngagementCohortMutationVariables,
          cache,
          _info
        ) => {
          cache.invalidate('Query', 'engagementCohorts');
          affectedCompanyIds.forEach((companyId) =>
            patchSupplierInCache(
              cache,
              createSupplierId({ companyId }),
              (gqlSupplier) => gqlSupplier,
              (supplier) => ({
                ...supplier,
                engagementCohortIds: supplier.engagementCohortIds.filter(
                  (engagementCohortId) =>
                    engagementCohortId !== args.input.cohortId
                ),
              })
            )
          );
        },
        updateEngagementTask(_result, _args, cache, _info) {
          invalidateAllTopLevelQueriesForNode(
            cache,
            'financeMeasurementProgressRows'
          );
          invalidateAllTopLevelQueriesForNode(
            cache,
            'supplierEmissionsFactorsTableRows'
          );
        },
        updateEngagementTaskConfig(result, args, cache, info) {
          invalidateAllTopLevelQueriesForNode(cache, 'engagementTaskConfigs');
        },
        upsertCompanyPortalSettings(_result, _args, cache, _info) {
          invalidateAllTopLevelQueriesForNode(cache, 'companyPortalSettings');
        },
        joinWaitlist(_result, _args, cache, _info) {
          cache.invalidate('Query', 'productWaitlistTypes');
        },
        leaveWaitlist(_result, _args, cache, _info) {
          cache.invalidate('Query', 'productWaitlistTypes');
        },
        joinReportWaitlist(_result, _args, cache, _info) {
          cache.invalidate('Query', 'reportWaitlistTypes');
        },
        leaveReportWaitlist(_result, _args, cache, _info) {
          cache.invalidate('Query', 'reportWaitlistTypes');
        },
        setAssetBenchmarks(_result, _args, cache, _info) {
          invalidateAllTopLevelQueriesForNode(cache, 'assetCorporate');
        },

        createMarketplaceEacPurchase(_result, _args, cache, _info) {
          cache.invalidate('Query', 'marketplacePurchases');
        },
        createPrivateDisclosure(
          result: GQCreatePrivateDisclosureMutation,
          _args,
          cache,
          _info
        ) {
          invalidateAllTopLevelQueriesForNode(
            cache,
            'suppliersWithTargetDisclosures'
          );
          const disclosure = result.createPrivateDisclosure?.disclosure;
          // private disclosures *should* have a company but it is not
          // guaranteed by our types today
          if (disclosure && disclosure.company) {
            updateSuppliersDataFragment(
              cache,
              { companyId: disclosure.company.id },
              (supplier) => ({
                // TODO (fravic): Handle targets outside of the current disclosure
                // Maybe the mutation can return those other disclosures as well?
                ...supplier,
                // eslint-disable-next-line @watershed/no-join-commas
                targets: (disclosure.targets ?? [])
                  .map((t) => t.description)
                  .join(', '),
              })
            );

            // Update the GetSupplierDetailsData query to include the new target
            cache.updateQuery(
              {
                query: GetSupplierDetailsDataDocument,
                variables: {
                  companyId: disclosure.company.id,
                  orgId: disclosure.privateDisclosure?.orgId,
                },
              },
              (data: GQGetSupplierDetailsDataQuery | null) => {
                if (!data) return null;
                return {
                  ...data,
                  targets: [...(data.targets || []), disclosure],
                };
              }
            );
          }
        },
        updatePrivateDisclosure(
          result: GQUpdatePrivateDisclosureMutation,
          _args,
          cache,
          _info
        ) {
          invalidateAllTopLevelQueriesForNode(
            cache,
            'suppliersWithTargetDisclosures'
          );
          const disclosure = result.updatePrivateDisclosure?.disclosure;
          // private disclosures *should* have a company but it is not
          // guaranteed by our types today
          if (disclosure && disclosure.company) {
            updateSuppliersDataFragment(
              cache,
              { companyId: disclosure.company.id },
              (supplier) => ({
                // TODO (fravic): Handle targets outside of the current disclosure
                // Maybe the mutation can return those other disclosures as well?
                ...supplier,
                // eslint-disable-next-line @watershed/no-join-commas
                targets: (disclosure.targets ?? [])
                  .map((t) => t.description)
                  .join(', '),
              })
            );

            // Update the GetSupplierDetailsData query to have the updated targets
            cache.updateQuery(
              {
                query: GetSupplierDetailsDataDocument,
                variables: {
                  companyId: disclosure.company.id,
                  orgId: disclosure.privateDisclosure?.orgId,
                },
              },
              (data: GQGetSupplierDetailsDataQuery | null) => {
                if (!data) return null;
                return {
                  ...data,
                  targets: data.targets?.map((target) =>
                    target.id === disclosure.id ? disclosure : target
                  ) || [disclosure],
                };
              }
            );
          }
        },
        deletePrivateDisclosureTargetById(
          result: GQDeletePrivateDisclosureTargetByIdMutation,
          _args,
          cache,
          _info
        ) {
          const { disclosure, targetId } =
            result.deletePrivateDisclosureTargetById ?? {
              disclosure: null,
              targetId: null,
            };
          // private disclosures *should* have a company but it is not
          // guaranteed by our types today
          if (disclosure && disclosure.company) {
            updateSuppliersDataFragment(
              cache,
              { companyId: disclosure.company.id },
              (supplier) => ({
                ...supplier,
                disclosures: supplier.targetDisclosures?.map(
                  (cachedDisclosure) => {
                    if (cachedDisclosure.id === disclosure.id) {
                      return {
                        ...cachedDisclosure,
                        targets:
                          cachedDisclosure.targets?.filter(
                            (target) => target.id !== targetId
                          ) || null,
                      };
                    }
                    return cachedDisclosure;
                  }
                ),
                // eslint-disable-next-line @watershed/no-join-commas
                targets: (disclosure.targets ?? [])
                  .filter((t) => t.id !== targetId)
                  .map((t) => t.description)
                  .join(', '),
              })
            );

            // Update the GetSupplierDetailsData query to have the updated targets
            cache.updateQuery(
              {
                query: GetSupplierDetailsDataDocument,
                variables: {
                  companyId: disclosure.company.id,
                  orgId: disclosure.privateDisclosure?.orgId,
                },
              },
              (data: GQGetSupplierDetailsDataQuery | null) => {
                if (!data) return null;
                return {
                  ...data,
                  targets: data.targets?.map((cachedDisclosure) => {
                    if (cachedDisclosure.id === disclosure.id) {
                      return {
                        ...cachedDisclosure,
                        targets:
                          cachedDisclosure.targets?.filter(
                            (target) => target.id !== targetId
                          ) || null,
                      };
                    }
                    return cachedDisclosure;
                  }),
                };
              }
            );
          }
        },
        acceptFinanceImportDiff: (_result, _args, cache, _info) => {
          // This is a temporary solution to invalidate the cache for the
          // finance import diff, which could impact lots of models. There
          // should be a more targeted way to invalidate the cache for only
          // the models that were impacted by the finance import diff.
          invalidateAllTopLevelQueriesForNode(cache, 'funds');
          invalidateAllTopLevelQueriesForNode(cache, 'assetsCorporate');
          invalidateAllTopLevelQueriesForNode(cache, 'assetsRealEstate');
          invalidateAllTopLevelQueriesForNode(cache, 'assetGroups');
          invalidateAllTopLevelQueriesForNode(cache, 'assetSovereignBonds');
          invalidateAllTopLevelQueriesForNode(
            cache,
            'assetPersonalMotorVehicleInsurances'
          );
          invalidateAllTopLevelQueriesForNode(cache, 'assetYears');
          invalidateAllTopLevelQueriesForNode(cache, 'sovereignBondAssetYears');
          invalidateAllTopLevelQueriesForNode(
            cache,
            'personalMotorVehicleInsuranceAssetYears'
          );
          invalidateAllTopLevelQueriesForNode(cache, 'assetHoldings');
          invalidateAllTopLevelQueriesForNode(
            cache,
            'personalMotorVehicleInsuranceAssetHoldings'
          );
          invalidateAllTopLevelQueriesForNode(cache, 'contacts');
          invalidateAllTopLevelQueriesForNode(
            cache,
            'financeModelCardMetadata'
          );
        },

        // Production graph mutations //
        acceptMaterialMasterDiff: (_result, _args, cache, _info) => {
          // Invalidate material variants cache to ensure updated data is fetched
          // after accepting material master diff changes
          invalidateAllTopLevelQueriesForNode(cache, 'materialVariants');
          // Invalidate the current MMV modification status to update the UI with the latest data
          invalidateAllTopLevelQueriesForNode(
            cache,
            'currentMmvModificationStatus'
          );
        },

        selectLcaForMaterialVariant: (result, args, cache, _info) => {
          // Invalidate only the specific material variant that was updated to
          // avoid unnecessarily refetching all material variants. We have to
          // search material variants by lineage ID instead of the ID of the
          // variant on the `materialVariantLca` object since a LCA connected to
          // a different material variant with the same lineage ID could have
          // been selected.
          const selectLcaResult =
            result as GQSelectLcaForMaterialVariantMutation;
          const allFields = cache.inspectFields('Query');
          const materialVariantsQueries = allFields.filter(
            (x) => x.fieldName === 'materialVariants'
          );
          for (const query of materialVariantsQueries) {
            const { fieldKey } = query;
            const cacheReference = cache.resolve(
              { __typename: 'Query' },
              fieldKey
            );
            // We expect cacheReference to be an array of material variant references
            if (!Array.isArray(cacheReference)) {
              continue;
            }
            const relevantValue = cacheReference
              .map((ref) =>
                cache.readFragment<GQMaterialTableFieldsFragment>(
                  MaterialTableFieldsFragmentDoc,
                  ref
                )
              )
              .find(
                (val: GQMaterialTableFieldsFragment | null) =>
                  val?.lineageId ===
                  selectLcaResult?.selectLcaForMaterialVariant
                    ?.materialVariantLineageId
              );
            if (relevantValue) {
              cache.invalidate({
                __typename: 'MaterialVariant',
                id: relevantValue.id,
              });
            }
          }
          // Invalidate the current MMV modification status to update the UI with the latest data
          invalidateAllTopLevelQueriesForNode(
            cache,
            'currentMmvModificationStatus'
          );
        },

        deleteMaterialVariants: (
          _result: GQDeleteMaterialVariantsMutation,
          args: GQDeleteMaterialVariantsMutationVariables,
          cache,
          _info
        ) => {
          // Remove the deleted material variants from the cached list of material variants
          // Use the input id list because the returned ids are the new slowly changing dimension ids
          const deletedIds = args.input.ids;

          cache.updateQuery(
            { query: GetMaterialVariantsDocument },
            (data: GQGetMaterialVariantsQuery | null) => {
              if (data?.materialVariants === undefined) {
                return data;
              }
              data.materialVariants = data.materialVariants.filter(
                (materialVariant) => !deletedIds.includes(materialVariant.id)
              );
              return data;
            }
          );
          // Invalidate the current MMV modification status to update the UI with the latest data
          invalidateAllTopLevelQueriesForNode(
            cache,
            'currentMmvModificationStatus'
          );
        },
        updateMaterialVariants: (result, args, cache, _info) => {
          // Invalidate the current MMV modification status to update the UI with the latest data
          invalidateAllTopLevelQueriesForNode(
            cache,
            'currentMmvModificationStatus'
          );
        },
        mapMaterialVariantsToAbstractMaterials: (
          result,
          args,
          cache,
          _info
        ) => {
          // Invalidate material variants cache to ensure updated data is fetched
          // after mapping material variants to abstract materials
          const mappingArgs =
            args as GQMapMaterialVariantsToAbstractMaterialsMutationVariables;
          for (const mappingInput of mappingArgs.input.mappingInputs) {
            cache.invalidate({
              __typename: 'MaterialVariant',
              id: mappingInput.materialVariantId,
            });
          }
          // Invalidate the current MMV modification status to update the UI with the latest data
          invalidateAllTopLevelQueriesForNode(
            cache,
            'currentMmvModificationStatus'
          );
        },
        createSupplierLcaUploadLcas: (result, args, cache, _info) => {
          // Invalidate the current MMV modification status to update the UI with the latest data
          invalidateAllTopLevelQueriesForNode(
            cache,
            'currentMmvModificationStatus'
          );
        },
        startMaterialMappingForUpdatedVariants: (
          result,
          _args,
          cache,
          _info
        ) => {
          const runningCount = (
            result as GQStartMaterialMappingForUpdatedVariantsMutation
          ).startMaterialMappingForUpdatedVariants.materialVariants.length;
          updateMaterialVariantsRunProgressCache(
            cache,
            runningCount,
            'proxyMapping'
          );
        },
        startFlashForMaterialVariant: (result, _args, cache, _info) => {
          if (
            !(result as GQStartFlashForMaterialVariantMutation)
              .startFlashForMaterialVariant?.materialVariant
          )
            updateMaterialVariantsRunProgressCache(cache, 1, 'flash');
        },
        startMaterialMappingForMaterialVariants: (
          result,
          _args,
          cache,
          _info
        ) => {
          const runningCount = (
            result as GQStartMaterialMappingForMaterialVariantsMutation
          ).startMaterialMappingForMaterialVariants.materialVariants.length;
          updateMaterialVariantsRunProgressCache(
            cache,
            runningCount,
            'proxyMapping'
          );
        },
        saveProductionGraph: (result, args, cache, _info) => {
          // Invalidate material variants. Since a production graph produces a
          // LCA, which could be tied to multiple material variants, we just
          // refresh all material variants.
          invalidateAllTopLevelQueriesForNode(cache, 'materialVariant');
        },
        createMaterialMethodologyVersionWithArtifacts: (
          result,
          args,
          cache
        ) => {
          // Invalidate the current MMV modification status to update the UI with the latest data
          invalidateAllTopLevelQueriesForNode(
            cache,
            'currentMmvModificationStatus'
          );
        },

        generateFootprintV2: (
          result,
          args: GQGenerateFootprintV2MutationVariables,
          cache,
          _info
        ) => {
          const generateFootprintResult =
            result as GQGenerateFootprintV2Mutation;
          const { id: footprintAssemblyRunId } =
            generateFootprintResult.generateFootprintV2.footprintAssemblyRun;

          updateCacheAfterFootprintGeneration(
            cache,
            args.input.footprintId,
            footprintAssemblyRunId
          );
        },

        prepareActivityDataAndRunFootprint: (
          result,
          args: GQPrepareActivityDataAndRunFootprintMutationVariables,
          cache,
          _info
        ) => {
          const prepareActivityDataResult =
            result as GQPrepareActivityDataAndRunFootprintMutation;

          const { id: footprintAssemblyRunId } =
            prepareActivityDataResult.prepareActivityDataAndRunFootprint
              .footprintAssemblyRun;

          updateCacheAfterFootprintGeneration(
            cache,
            args.input.footprintId,
            footprintAssemblyRunId
          );
        },

        generateCustomerUploadedReferenceDataRevision: (
          result,
          _args,
          cache,
          _info
        ) => {
          invalidateAllTopLevelQueriesForNode(
            cache,
            'methodologyCustomizationRowsForOrg'
          );
        },
        setPublishEngagementTaskConfig: (
          result: GQSetPublishEngagementTaskConfigMutation,
          args,
          cache,
          info
        ) => {
          // update cache for queries that don't
          // want unpublished org specific configs
          cache
            .inspectFields('Query')
            .filter((field) => field.fieldName === 'engagementTaskConfigs')
            .filter(
              (field) =>
                field.arguments &&
                !field.arguments['includeUnpublishedOrgSpecific']
            )
            .forEach((field) => {
              const { fieldName, arguments: variables } = field;
              cache.invalidate('Query', fieldName, variables || undefined);
            });
        },
        pushUtilitiesDiff(_result, _args, cache, info) {
          if (!info.error) {
            invalidateAllTopLevelQueriesForNode(cache, 'buildingWithUtilities');
            invalidateAllTopLevelQueriesForNode(cache, 'utilitiesMetadata');
            // During a file edit, utilities pushing might delete a file, which will update the changelog
            invalidateAllTopLevelQueriesForNode(
              cache,
              'dataApprovalUserUploadTaskChangelog'
            );
          }
        },
        createBuilding(_result, _args, cache, info) {
          invalidateAllTopLevelQueriesForNode(
            cache,
            'dataApprovalObjectChangelog'
          );
          invalidateAllTopLevelQueriesForNode(cache, 'measurementProject');
          invalidateAllTopLevelQueriesForNode(cache, 'organization');
          invalidateAllTopLevelQueriesForNode(cache, 'facilities');
          invalidateAllTopLevelQueriesForNode(
            cache,
            'facilitiesWithMissingFields'
          );
          invalidateAllTopLevelQueriesForNode(
            cache,
            'latestOrgUnitTypesForCanonicalDatasetKind'
          );
        },
        updateBuilding(_result, _args, cache, info) {
          invalidateAllTopLevelQueriesForNode(
            cache,
            'dataApprovalObjectChangelog'
          );
        },
        updateBuildings(_result, _args, cache, info) {
          invalidateAllTopLevelQueriesForNode(
            cache,
            'dataApprovalObjectChangelog'
          );
        },
        deleteBuildings(_result, _args, cache, info) {
          invalidateAllTopLevelQueriesForNode(
            cache,
            'dataApprovalObjectChangelog'
          );
          invalidateAllTopLevelQueriesForNode(cache, 'facilities');
        },
        saveDeltaProgressValueMapping(_result, _args, cache, info) {
          invalidateAllTopLevelQueriesForNode(
            cache,
            'valueMappingAggregatedQuantities'
          );
        },
        approveAllValueMappingRecords(_result, _args, cache, info) {
          invalidateAllTopLevelQueriesForNode(
            cache,
            'datasourceValueMappingRule'
          );
        },
        upsertFootprintExclusionRulesV2(_result, _args, cache, info) {
          invalidateAllTopLevelQueriesForNode(cache, 'footprintExclusionRules');
        },
        upsertFootprintCategoryRules(_result, _args, cache, info) {
          invalidateAllTopLevelQueriesForNode(cache, 'footprintCategoryRules');
        },

        createWatershedFootprintReviewRequest: (result, args, cache, info) => {
          const payload =
            result.createWatershedFootprintReviewRequest as GQCreateWatershedFootprintReviewRequestPayload | null;
          if (!payload) return;

          // Handle everything we can incrementally
          updateCacheAfterCreateWatershedFootprintReviewRequest(cache, payload);

          // Invalidate the footprints list since we don't have a footprint id
          invalidateAllTopLevelQueriesForNode(cache, 'footprints');
        },

        updateWatershedFootprintReviewRequestStatus(
          result: GQUpdateFootprintReviewRequestStatusMutation,
          args: GQUpdateFootprintReviewRequestStatusMutationVariables,
          cache
        ) {
          const payload = result.updateWatershedFootprintReviewRequestStatus;

          // 1. Update the footprint selector status
          cache.updateQuery(
            {
              query: FootprintsForFootprintSelectorDocument,
              variables: {},
            },
            (data: GQFootprintsForFootprintSelectorQuery | null) => {
              return mergeWatershedFootprintReviewRequestIntoFootprintSelector(
                data,
                payload
              );
            }
          );

          // 2. Invalidate data on the footprints list page - we can't be
          //    precise because we don't have a footprint id here
          invalidateAllTopLevelQueriesForNode(cache, 'footprints');
        },

        deleteWatershedFootprintReviewRequest(
          result: GQCancelFootprintReviewRequestMutation,
          args: GQCancelFootprintReviewRequestMutationVariables,
          cache,
          info
        ) {
          const payload = result.deleteWatershedFootprintReviewRequest;

          // 1. Update footprint selector status
          cache.updateQuery(
            {
              query: FootprintsForFootprintSelectorDocument,
              variables: {},
            },
            (data: GQFootprintsForFootprintSelectorQuery | null) => {
              return mergeCancelWatershedFootprintReviewRequestIntoFootprintSelector(
                data,
                payload
              );
            }
          );

          // 2. Invalidate data on the footprints list page - we can't be
          //    precise because we don't have a footprint id here
          invalidateAllTopLevelQueriesForNode(cache, 'footprints');
        },

        setPreferredLocale(
          _result: GQSetPreferredLocaleMutation,
          args: GQSetPreferredLocaleMutationVariables,
          cache
        ) {
          onSetLocale(args.input.locale);
          cache.invalidate('Query');
        },
        checkAndGenerateFacilitiesBarts(
          _result: GQCheckAndGenerateFacilitiesBartsMutation,
          _args,
          cache
        ) {
          // At this point, we'll be running a background job to refresh
          // facilities BARTs, and the facilities data summaries UI will be
          // blocked. We invlidate the data summary queries here so that when
          // the UI is unblocked, the query is re-run. We're invalidating *all*
          // BI queries here (not just facilities) but these queries are
          // relatively fast anyway, and I don't want to have to worry about
          // keeping the query variables in sync here.
          invalidateAllTopLevelQueriesForNode(cache, 'biQueryDataV2');
        },
        upsertOrgStructureVersion(
          result: GQUpsertOrgStructureVersionMutation,
          args: GQUpsertOrgStructureVersionMutationVariables,
          cache
        ) {
          cache.updateQuery(
            {
              query: OrgStructureVersionsDocument,
            },
            (data: GQOrgStructureVersionsQuery | null) => {
              if (data) {
                // we only need to update the cache if something was created,
                // a.k.a. we didn't pass an id
                // This is because cache updates are free for update queries
                // are free using urql (i.e. an id and __typename were returned)
                if (!args.input.id) {
                  data.orgStructureVersions.push(
                    result.upsertOrgStructureVersion.orgStructureVersion
                  );
                }
              }
              return data;
            }
          );
        },
        deleteOrgStructureVersion(
          result: GQDeleteOrgStructureVersionMutation,
          _args: GQDeleteOrgStructureVersionMutationVariables,
          cache
        ) {
          cache.updateQuery(
            {
              query: OrgStructureVersionsDocument,
            },
            (data: GQOrgStructureVersionsQuery | null) => {
              if (data) {
                data.orgStructureVersions = data.orgStructureVersions.filter(
                  (version) =>
                    version.id !==
                    result.deleteOrgStructureVersion.orgStructureVersion.id
                );
              }
              return data;
            }
          );
        },
        duplicateOrgStructureVersion(
          result: GQDuplicateOrgStructureVersionMutation,
          _args: GQDuplicateOrgStructureVersionMutationVariables,
          cache
        ) {
          cache.updateQuery(
            {
              query: OrgStructureVersionsDocument,
            },
            (data: GQOrgStructureVersionsQuery | null) => {
              if (data) {
                data.orgStructureVersions.push(
                  result.duplicateOrgStructureVersion.orgStructureVersion
                );
              }
              return data;
            }
          );
        },
        createDefaultOrgStructureVersion(
          result: GQCreateDefaultOrgStructureVersionMutation,
          _args: GQCreateDefaultOrgStructureVersionMutationVariables,
          cache
        ) {
          cache.updateQuery(
            {
              query: OrgStructureVersionsDocument,
            },
            (data: GQOrgStructureVersionsQuery | null) => {
              if (data) {
                data.orgStructureVersions.push(
                  result.createDefaultOrgStructureVersion.orgStructureVersion
                );
              }
              return data;
            }
          );
        },
        createOrgUnitType(
          result: GQCreateOrgUnitTypeMutation,
          args: GQCreateOrgUnitTypeMutationVariables,
          cache
        ) {
          cache.invalidate('OrgUnitType');
        },
        deleteOrgUnitType(
          result: GQDeleteOrgUnitTypeMutation,
          args: GQDeleteOrgUnitTypeMutationVariables,
          cache
        ) {
          cache.invalidate({
            __typename: 'OrgUnitType',
            id: result.deleteOrgUnitType.orgUnitType.id,
          });
          cache.invalidate('OrgUnitTypeRelationship');
        },
        createOrgUnit(
          result: GQCreateOrgUnitMutation,
          args: GQCreateOrgUnitMutationVariables,
          cache
        ) {
          cache.invalidate('OrgUnit');
          cache.updateQuery(
            {
              query: OrgUnitTypeDocument,
              variables: {
                versionId: args.input.versionId,
                orgUnitTypeId: args.input.orgUnitTypeId,
              },
            },
            (data: GQOrgUnitTypeQuery | null) => {
              if (data) {
                const orgUnitType = data.orgUnitTypes.find(
                  ({ id }) => id === args.input.orgUnitTypeId
                );
                if (orgUnitType) {
                  orgUnitType.orgUnits.push(result.createOrgUnit.orgUnit);
                }
              }
              return data;
            }
          );
        },
        deleteOrgUnits(
          result: GQDeleteOrgUnitsMutation,
          args: GQDeleteOrgUnitsMutationVariables,
          cache
        ) {
          invalidateDeletedRecords(
            cache,
            result.deleteOrgUnits.orgUnits.map((unit) => unit.id),
            'OrgUnit'
          );
        },
        createOrgUnitTypeRelationship(
          result: GQCreateOrgUnitTypeRelationshipMutation,
          args: GQCreateOrgUnitTypeRelationshipMutationVariables,
          cache
        ) {
          cache.invalidate({
            __typename: 'OrgUnitType',
            id: args.input.orgUnitTypeIdFrom,
          });
          cache.invalidate({
            __typename: 'OrgUnitType',
            id: args.input.orgUnitTypeIdTo,
          });
        },
        deleteOrgUnitTypeRelationship(
          result: GQDeleteOrgUnitTypeRelationshipMutation,
          args: GQDeleteOrgUnitTypeRelationshipMutationVariables,
          cache
        ) {
          cache.invalidate({
            __typename: 'OrgUnitType',
            id: result.deleteOrgUnitTypeRelationship.orgUnitTypeRelationship
              .orgUnitTypeIdFrom,
          });
          cache.invalidate({
            __typename: 'OrgUnitType',
            id: result.deleteOrgUnitTypeRelationship.orgUnitTypeRelationship
              .orgUnitTypeIdTo,
          });
          cache.invalidate({
            __typename: 'OrgUnitTypeRelationship',
            id: result.deleteOrgUnitTypeRelationship.orgUnitTypeRelationship.id,
          });
        },
        createOrgUnitRelationship(
          result: GQCreateOrgUnitRelationshipMutation,
          args: GQCreateOrgUnitRelationshipMutationVariables,
          cache
        ) {
          cache.invalidate({
            __typename: 'OrgUnit',
            id: args.input.orgUnitIdFrom,
          });
          cache.invalidate({
            __typename: 'OrgUnit',
            id: args.input.orgUnitIdTo,
          });
        },
        acceptOrgStructureVersionUpload(
          _result: GQAcceptOrgStructureVersionUploadMutation,
          _args: GQAcceptOrgStructureVersionUploadMutationVariables,
          cache
        ) {
          invalidateAllTopLevelQueriesForNode(cache, 'orgUnits');
          invalidateAllTopLevelQueriesForNode(cache, 'orgUnitRelationships');
        },
        assignMeasurementTask(
          _result: GQAssignMeasurementTaskMutation,
          _args: GQAssignMeasurementTaskMutationVariables,
          cache
        ) {
          invalidateAllTopLevelQueriesForNode(cache, 'users');
        },
        setApprovers(
          _result: GQSetApproversMutation,
          _args: GQSetApproversMutationVariables,
          cache
        ) {
          invalidateAllTopLevelQueriesForNode(cache, 'users');
        },
        addDataApprovalComment(
          result: GQAddDataApprovalCommentMutation,
          args: GQAddDataApprovalCommentMutationVariables,
          cache
        ) {
          if (args.input.buildingId) {
            updateDataApprovalObjectChangelogCache(
              cache,
              args.input.buildingId,
              GQObjectType.Building,
              result.addDataApprovalComment.newChangelogEvents
            );
          }
          updateDataApprovalUserUploadTaskChangelogCache(
            cache,
            'UserUploadTask',
            args.input.userUploadTaskId,
            result.addDataApprovalComment.newChangelogEvents
          );
        },
        deleteFile(
          _result: GQDeleteFileMutation,
          args: GQDeleteFileMutationVariables,
          cache
        ) {
          invalidateAllTopLevelQueriesForNode(
            cache,
            'dataApprovalUserUploadTaskChangelog'
          );
        },
        deleteSupportingDocument(
          _result: GQDeleteBuildingSupportingDocumentMutation,
          args: GQDeleteBuildingSupportingDocumentMutationVariables,
          cache
        ) {
          // When a supporting document is deleted, invalidate all buildingWithUtilities queries
          // so that the PaginatedUtilityRows query (used in UtilitiesPreviewCard) gets fresh data
          invalidateAllTopLevelQueriesForNode(cache, 'buildingWithUtilities');
        },
        generateBartFromFileUpload(
          _result: GQGenerateBartFromFileUploadMutation,
          args: GQGenerateBartFromFileUploadMutationVariables,
          cache
        ) {
          // During a file edit, BART generation might delete a file, which will update the changelog
          invalidateAllTopLevelQueriesForNode(
            cache,
            'dataApprovalUserUploadTaskChangelog'
          );
        },
        requestDataApproval(
          result: GQRequestDataApprovalMutation,
          args: GQRequestDataApprovalMutationVariables,
          cache
        ) {
          updateDataApprovalUserUploadTaskChangelogCache(
            cache,
            args.input.targetKind,
            args.input.targetId,
            result.requestDataApproval.newChangelogEvents
          );
        },
        approveData(
          result: GQApproveDataMutation,
          args: GQApproveDataMutationVariables,
          cache
        ) {
          updateDataApprovalUserUploadTaskChangelogCache(
            cache,
            args.input.targetKind,
            args.input.targetId,
            result.approveData.newChangelogEvents
          );
        },
        createMaterialityAssessment(_result, _args, cache) {
          invalidateAllTopLevelQueriesForNode(
            cache,
            'allMaterialityAssessments'
          );
        },
        deleteMaterialityAssessment(
          result: GQDeleteMaterialityAssessmentMutation,
          _args,
          cache
        ) {
          cache.updateQuery(
            {
              query: MaterialityPageDocument,
              variables: {
                input: {},
              },
            },
            (data: GQMaterialityPageQuery | null) => {
              if (data) {
                data.allMaterialityAssessments =
                  result.deleteMaterialityAssessment.materialityAssessments;
              }
              return data;
            }
          );
        },
        updateMaterialityAssessment(
          result: GQUpdateMaterialityAssessmentMutation,
          _args,
          cache
        ) {
          cache.updateQuery(
            {
              query: MaterialityPageDocument,
              variables: {
                input: {},
              },
            },
            (data: GQMaterialityPageQuery | null) => {
              if (data) {
                data.allMaterialityAssessments =
                  result.updateMaterialityAssessment.materialityAssessments;
              }
              return data;
            }
          );
        },
        rejectData(
          result: GQRejectDataMutation,
          args: GQRejectDataMutationVariables,
          cache
        ) {
          updateDataApprovalUserUploadTaskChangelogCache(
            cache,
            args.input.targetKind,
            args.input.targetId,
            result.rejectData.newChangelogEvents
          );
        },
        requestUnlockData(
          result: GQRequestUnlockDataMutation,
          args: GQRequestUnlockDataMutationVariables,
          cache
        ) {
          updateDataApprovalUserUploadTaskChangelogCache(
            cache,
            args.input.targetKind,
            args.input.targetId,
            result.requestUnlockData.newChangelogEvents
          );
        },
        unlockData(
          result: GQUnlockDataMutation,
          args: GQUnlockDataMutationVariables,
          cache
        ) {
          updateDataApprovalUserUploadTaskChangelogCache(
            cache,
            args.input.targetKind,
            args.input.targetId,
            result.unlockData.newChangelogEvents
          );
        },
        assignFacilitiesToTasks(_result, _args, cache, info) {
          invalidateAllTopLevelQueriesForNode(cache, 'facilities');
        },
        unassignFacilitiesFromTask(_result, _args, cache, info) {
          invalidateAllTopLevelQueriesForNode(cache, 'facilities');
        },
        deleteUserUploadTask(_result, _args, cache, info) {
          invalidateAllTopLevelQueriesForNode(cache, 'facilities');
        },
        deleteUserUploadTasks(_result, _args, cache, info) {
          invalidateAllTopLevelQueriesForNode(cache, 'facilities');
        },
        createCtsvOneSchemaFileFeed(_result, _args, cache, info) {
          invalidateAllTopLevelQueriesForNode(cache, 'userUploadCandidate');
        },
        deleteCtsvOneSchemaFileFeed(_result, _args, cache, info) {
          invalidateAllTopLevelQueriesForNode(cache, 'userUploadCandidate');
        },

        /**
         * It is sad that we have to do this, but it is caused by the downstream
         * pagination params on the query :/
         */
        updateSupportCase(
          result: GQUpdateSupportCaseMutation,
          args: GQUpdateSupportCaseMutationVariables,
          cache
        ) {
          if (result.updateSupportCase?.supportCase.timeline) {
            cache.updateQuery(
              {
                query: SupportCasePageDocument,
                variables: {
                  id: result.updateSupportCase.supportCase.id,
                  after: null,
                  before: null,
                  first: 100,
                  last: null,
                },
              },
              (data: GQSupportCasePageQuery | null) => {
                if (data) {
                  data.supportCase.timeline =
                    result.updateSupportCase.supportCase.timeline;
                }
                return data;
              }
            );
          }
        },
        createSupportCaseComment(
          result: GQCreateSupportCaseCommentMutation,
          _args: any,
          cache
        ) {
          if (result.createSupportCaseComment?.supportCase.timeline) {
            cache.updateQuery(
              {
                query: SupportCasePageDocument,
                variables: {
                  id: result.createSupportCaseComment.supportCase.id,
                  after: null,
                  before: null,
                  first: 100,
                  last: null,
                },
              },
              (data: GQSupportCasePageQuery | null) => {
                if (data) {
                  data.supportCase.timeline =
                    result.createSupportCaseComment.supportCase.timeline;
                }
                return data;
              }
            );
          }
        },
        bulkAssignFacilitiesToTasks(_result, _args, cache, info) {
          invalidateAllTopLevelQueriesForNode(cache, 'facilities');
          invalidateAllTopLevelQueriesForNode(cache, 'measurementProject');
        },
        batchUpdateReportQuestionAssignees(
          result: GQBatchUpdateReportQuestionAssigneesMutation,
          args_: GQBatchUpdateReportQuestionAssigneesMutationVariables,
          cache,
          info
        ) {
          if (info.error) return;

          cache.invalidate('ReportQuestionInstancesAggregate');

          for (const question of result.batchUpdateReportQuestionAssignees
            .questions) {
            cache.invalidate(
              {
                __typename: 'ReportQuestionInstance',
                id: question.instance.id,
              },
              'userAccess'
            );
          }
        },
        upsertReportsAccess(
          _,
          args: GQMutationUpsertReportsAccessArgs,
          cache,
          info
        ) {
          if (info.error) return;

          for (const id of args.input.reportIds) {
            cache.invalidate({ __typename: 'Report', id }, 'userAccess');
          }
        },

        upsertReportQuestionInstancesAccess(
          _,
          args: GQUpsertReportQuestionInstancesAccessMutationVariables,
          cache,
          info
        ) {
          if (info.error) return;

          cache.invalidate('ReportQuestionInstancesAggregate');

          for (const id of args.input.reportQuestionInstanceIds) {
            cache.invalidate(
              { __typename: 'ReportQuestionInstance', id },
              'userAccess'
            );
          }
        },

        createCalculationCitation: (
          result: GQCreateCalculationCitationMutation,
          _args,
          cache,
          _info
        ) => {
          const payload = result.createCalculationCitation;
          if (payload?.citation) {
            cache.updateQuery(
              { query: CalculationCitationsForOrgDocument },
              (data: GQCalculationCitationsForOrgQuery | null) => {
                if (data?.calculationCitationsForOrg) {
                  data.calculationCitationsForOrg.push(payload.citation);
                }

                if (data?.citationSlugsForOrg && payload.citation.orgId) {
                  data.citationSlugsForOrg.push(
                    removeOrgSuffixFromSlug({
                      orgId: payload.citation.orgId,
                      slug: payload.citation.slug,
                    })
                  );
                }

                return data;
              }
            );
          }
        },

        archiveCalculationCitation: (
          result: GQArchiveCalculationCitationMutation,
          args,
          cache,
          info
        ) => {
          const payload = result.archiveCalculationCitation;
          if (!payload) return;

          cache.updateQuery(
            { query: CalculationCitationsForOrgDocument },
            (data: GQCalculationCitationsForOrgQuery | null) => {
              if (data === null) return null;

              data.calculationCitationsForOrg =
                data.calculationCitationsForOrg.filter(
                  (record) => record?.id !== payload.citation.id
                );

              return data;
            }
          );

          cache.updateQuery(
            { query: ArchivedCalculationCitationsForOrgDocument },
            (data: GQArchivedCalculationCitationsForOrgQuery | null) => {
              if (data?.archivedCalculationCitationsForOrg) {
                data.archivedCalculationCitationsForOrg.push(payload.citation);
              }
              return data;
            }
          );
        },

        unarchiveCalculationCitation: (
          result: GQUnarchiveCalculationCitationMutation,
          args,
          cache,
          info
        ) => {
          const payload = result.unarchiveCalculationCitation;
          if (!payload) return;

          cache.updateQuery(
            { query: CalculationCitationsForOrgDocument },
            (data: GQCalculationCitationsForOrgQuery | null) => {
              if (data?.calculationCitationsForOrg) {
                data.calculationCitationsForOrg.push(payload.citation);
              }
              return data;
            }
          );

          cache.updateQuery(
            { query: ArchivedCalculationCitationsForOrgDocument },
            (data: GQArchivedCalculationCitationsForOrgQuery | null) => {
              if (data === null) return null;

              data.archivedCalculationCitationsForOrg =
                data.archivedCalculationCitationsForOrg.filter(
                  (record) => record?.id !== payload.citation.id
                );

              return data;
            }
          );
        },

        archiveCalculationCustomization: (
          result: GQArchiveCalculationCustomizationMutation,
          _args,
          cache,
          _info
        ) => {
          const payload = result.archiveCalculationCustomization;
          if (!payload?.customization) return;

          // Remove from non-archived list (archivedOnly: false)
          cache.updateQuery(
            {
              query: CalculationCustomizationsDocument,
              variables: { includeNonUserVisible: false, archivedOnly: false },
            },
            (data: GQCalculationCustomizationsQuery | null) => {
              if (data === null) return null;

              data.calculationCustomizations =
                data.calculationCustomizations.filter(
                  (
                    customization: GQCalculationCustomizationsQuery['calculationCustomizations'][number]
                  ) => customization.id !== payload.customization.id
                );

              return data;
            }
          );

          // Add to archived list (archivedOnly: true)
          cache.updateQuery(
            {
              query: CalculationCustomizationsDocument,
              variables: { includeNonUserVisible: false, archivedOnly: true },
            },
            (data: GQCalculationCustomizationsQuery | null) => {
              if (data === null) return null;

              // Only add if it's not already in the list
              const existingCustomization = data.calculationCustomizations.find(
                (
                  customization: GQCalculationCustomizationsQuery['calculationCustomizations'][number]
                ) => customization.id === payload.customization.id
              );
              if (!existingCustomization) {
                data.calculationCustomizations.push(payload.customization);
              }

              return data;
            }
          );
        },

        unarchiveCalculationCustomization: (
          result: GQUnarchiveCalculationCustomizationMutation,
          _args,
          cache,
          _info
        ) => {
          const payload = result.unarchiveCalculationCustomization;
          if (!payload?.customization) return;

          // Remove from archived list (archivedOnly: true)
          cache.updateQuery(
            {
              query: CalculationCustomizationsDocument,
              variables: { includeNonUserVisible: false, archivedOnly: true },
            },
            (data: GQCalculationCustomizationsQuery | null) => {
              if (data === null) return null;

              data.calculationCustomizations =
                data.calculationCustomizations.filter(
                  (
                    customization: GQCalculationCustomizationsQuery['calculationCustomizations'][number]
                  ) => customization.id !== payload.customization.id
                );

              return data;
            }
          );

          // Add to non-archived list (archivedOnly: false)
          cache.updateQuery(
            {
              query: CalculationCustomizationsDocument,
              variables: { includeNonUserVisible: false, archivedOnly: false },
            },
            (data: GQCalculationCustomizationsQuery | null) => {
              if (data === null) return null;

              // Only add if it's not already in the list
              const existingCustomization = data.calculationCustomizations.find(
                (
                  customization: GQCalculationCustomizationsQuery['calculationCustomizations'][number]
                ) => customization.id === payload.customization.id
              );
              if (!existingCustomization) {
                data.calculationCustomizations.push(payload.customization);
              }

              return data;
            }
          );
        },

        createReferenceTable: (
          result: GQCreateReferenceTableMutation,
          _args,
          cache,
          _info
        ) => {
          const payload = result.createReferenceTable;
          if (!payload?.referenceTable) return;

          cache.updateQuery(
            { query: ReferenceTablesForOrgPaginatedDocument },
            (data: GQReferenceTablesForOrgPaginatedQuery | null) => {
              if (!data?.referenceTablesForOrgPaginated) return null;

              data.referenceTablesForOrgPaginated.edges.unshift({
                __typename: 'ReferenceTableEdge',
                node: payload.referenceTable,
              });

              return data;
            }
          );
        },
      },
    },
    schema,
  });

export default cache;
